/**
 * v-gtm-events.js — GA4 tracking (skill-aligned)
 * - window.vGtm.push() for skill / new declarative events (data-v-gtm-*)
 * - window.GTMTrackerGA4 legacy API for existing data-ga4-* markup
 */
(function () {
  'use strict';

  const config = {
    debug: false,
    brand: 'Cosori',
    defaultCurrency: 'USD',
  };

  function log(...args) {
    if (config.debug) {
      // console.log('[GTM GA4]', ...args);
      console.log('[GTM GA4]', JSON.stringify(args));
    }
  }

  function normalizeEventParams(eventName, eventParameters) {
    const params = Object.assign({}, eventParameters);

    // Excel 规范用 link_url；声明式埋点常写 data-v-gtm-url / params.url（$url 占位符）
    if (eventName !== 'generate_lead') {
      if (params.url && !params.link_url) {
        params.link_url = params.url;
      }
      if (params.link_url) {
        delete params.url;
      }
    }

    return params;
  }

  function pushGA4Event(eventName, eventParameters = {}) {
    window.dataLayer = window.dataLayer || [];

    const params = normalizeEventParams(eventName, eventParameters);

    window.dataLayer.push({ event_parameters: null });
    window.dataLayer.push({
      event: 'ga4Event',
      event_name: eventName,
      event_parameters: params,
    });

    log('GA4 Event:', eventName, params);
  }

  function formatShopifyProductId(productId) {
    if (productId == null || productId === '') return '';
    const id = String(productId).trim();
    if (!id) return '';
    return id.charAt(0) === 'v' ? id.slice(1) : id;
  }

  function resolveItemProductId(itemOrProductId, productIdFallback) {
    if (itemOrProductId != null && typeof itemOrProductId === 'object') {
      return formatShopifyProductId(itemOrProductId.item_product_id || itemOrProductId.product_id);
    }
    return formatShopifyProductId(itemOrProductId || productIdFallback);
  }

  function formatShopifyVariantId(variantId) {
    if (variantId == null || variantId === '') return '';
    const id = String(variantId).trim();
    if (!id) return '';
    return id.charAt(0) === 'v' ? id.slice(1) : id;
  }

  function resolveItemVariantId(itemOrVariantId, variantIdFallback) {
    if (itemOrVariantId != null && typeof itemOrVariantId === 'object') {
      return formatShopifyVariantId(
        itemOrVariantId.item_variant_id || itemOrVariantId.variant_id || itemOrVariantId.variantId
      );
    }
    return formatShopifyVariantId(itemOrVariantId || variantIdFallback);
  }

  function formatItems(items, module = '') {
    if (!Array.isArray(items)) {
      items = [items];
    }

    return items.map((item) => {
      const listId = item.item_list_id || module || '';
      const listName = item.item_list_name || item.item_list_id || module || '';
      const formatted = {
        item_id: item.item_id || item.id || '',
        item_name: item.item_name || item.name || '',
        item_brand: config.brand,
        item_category: item.item_category || item.category || '',
        item_variant: item.item_variant || item.variant || '',
        price: parseFloat(item.price) || 0,
        quantity: parseInt(item.quantity) || 1,
        discount: parseFloat(item.discount) || 0,
        index: item.index || 0,
        item_list_id: listId,
        item_list_name: listName,
      };
      const itemProductId = resolveItemProductId(item);
      if (itemProductId) formatted.item_product_id = itemProductId;
      const itemVariantId = resolveItemVariantId(item);
      if (itemVariantId) formatted.item_variant_id = itemVariantId;
      return formatted;
    });
  }

  const GA4 = {
    viewItem: function (itemData, module = 'Product Details Page') {
      const items = formatItems(itemData, module);
      const value = Math.round(items.reduce((sum, item) => sum + item.price * item.quantity, 0) * 100) / 100;

      pushGA4Event('view_item', {
        currency: config.defaultCurrency,
        value: value,
        module: module,
        items: items,
      });
    },

    addToCart: function (itemData, params = {}) {
      const module = params.module || 'Product Details Page';
      const items = formatItems(itemData, module);
      const value = Math.round(items.reduce((sum, item) => sum + item.price * item.quantity, 0) * 100) / 100;

      pushGA4Event('add_to_cart', {
        currency: config.defaultCurrency,
        value: value,
        module: module,
        button_name: params.button_name || 'Add to Cart',
        items: items,
      });
    },

    removeFromCart: function (itemData, params = {}) {
      const module = params.module || 'Cart Popup';
      const items = formatItems(itemData, module);
      const value = Math.round(items.reduce((sum, item) => sum + item.price * item.quantity, 0) * 100) / 100;

      pushGA4Event('remove_from_cart', {
        module: module,
        currency: config.defaultCurrency,
        value: value,
        button_name: params.button_name || 'Remove',
        items: items,
      });
    },

    decreaseQuantity: function (itemData, params = {}) {
      const eventParams = {
        product_id: itemData.item_id || itemData.sku || '',
        product_name: itemData.item_name || itemData.product_name || '',
        quantity: params.quantity || 1,
      };
      const itemProductId = resolveItemProductId(itemData);
      if (itemProductId) eventParams.item_product_id = itemProductId;
      pushGA4Event('decrease_quantity', eventParams);
    },

    increaseQuantity: function (itemData, params = {}) {
      const eventParams = {
        product_id: itemData.item_id || itemData.sku || '',
        product_name: itemData.item_name || itemData.product_name || '',
        quantity: params.quantity || 1,
      };
      const itemProductId = resolveItemProductId(itemData);
      if (itemProductId) eventParams.item_product_id = itemProductId;
      pushGA4Event('increase_quantity', eventParams);
    },

    addContactInfo: function (params = {}) {
      const payload = Object.assign({}, params);
      if (payload.items) {
        payload.items = formatItems(payload.items, 'Checkout');
        payload.currency = payload.currency || config.defaultCurrency;
        if (payload.value == null) {
          payload.value =
            Math.round(payload.items.reduce((sum, item) => sum + item.price * item.quantity, 0) * 100) / 100;
        }
      }
      pushGA4Event('add_contact_info', payload);
    },

    addAddressInfo: function (params = {}) {
      const payload = Object.assign({}, params);
      if (payload.items) {
        payload.items = formatItems(payload.items, 'Checkout');
        payload.currency = payload.currency || config.defaultCurrency;
        if (payload.value == null) {
          payload.value =
            Math.round(payload.items.reduce((sum, item) => sum + item.price * item.quantity, 0) * 100) / 100;
        }
      }
      pushGA4Event('add_address_info', payload);
    },

    addShippingInfo: function (itemsData, params = {}) {
      const items = formatItems(itemsData, params.module || 'Checkout');
      const value = Math.round(items.reduce((sum, item) => sum + item.price * item.quantity, 0) * 100) / 100;

      pushGA4Event('add_shipping_info', {
        currency: params.currency || config.defaultCurrency,
        value: params.value != null ? parseFloat(params.value) : value,
        items: items,
      });
    },

    addPaymentInfo: function (itemsData, params = {}) {
      const items = formatItems(itemsData, params.module || 'Checkout');
      const value = Math.round(items.reduce((sum, item) => sum + item.price * item.quantity, 0) * 100) / 100;

      pushGA4Event('add_payment_info', {
        currency: params.currency || config.defaultCurrency,
        value: params.value != null ? parseFloat(params.value) : value,
        items: items,
      });
    },

    applyPromoCode: function (params = {}) {
      pushGA4Event('apply_promo_code', {
        button_name: params.button_name || '',
        coupon: params.coupon || params.promo_code || '',
        discount: params.discount || '',
        status: params.status || '',
        promo_code_entered: params.promo_code_entered || params.enter_code || null,
        error_message: params.error_message || params.reason || null,
      });
    },

    purchase: function (itemsData, params = {}) {
      const items = formatItems(itemsData, params.module);
      const value =
        params.value != null
          ? parseFloat(params.value)
          : Math.round(items.reduce((sum, item) => sum + item.price * item.quantity, 0) * 100) / 100;

      pushGA4Event('purchase', {
        currency: params.currency || config.defaultCurrency,
        value: value,
        transaction_id: params.transaction_id || '',
        items: items,
      });
    },

    viewItemList: function (itemsData, listName = '', module = '') {
      const items = formatItems(itemsData, module || listName);
      const value = Math.round(items.reduce((sum, item) => sum + item.price * item.quantity, 0) * 100) / 100;
      const listId = (items[0] && items[0].item_list_id) || module || listName;
      const listNameFinal = (items[0] && items[0].item_list_name) || module || listName;

      pushGA4Event('view_item_list', {
        item_list_id: listId,
        item_list_name: listNameFinal,
        module: module,
        currency: config.defaultCurrency,
        value: value,
        items: items,
      });
    },

    selectItem: function (itemData, params = {}) {
      const listId = params.item_list_id || params.item_list_name || params.module || '';
      const items = formatItems(itemData, listId);

      pushGA4Event('select_item', {
        module: params.module || '',
        item_list_id: listId,
        currency: config.defaultCurrency,
        button_name: params.button_name || '',
        items: items,
      });
    },


    viewCart: function (itemsData, params = {}) {
      const module = params.module || 'Cart Popup';
      const items = formatItems(itemsData, module);
      const value = Math.round(items.reduce((sum, item) => sum + item.price * item.quantity, 0) * 100) / 100;

      pushGA4Event('view_cart', {
        module: module,
        currency: config.defaultCurrency,
        value: value,
        items: items,
      });
    },

    addToWishlist: function (itemData, params = {}) {
      const module = params.module || 'Favorite Page';
      const items = formatItems(itemData, module);
      const value = Math.round(items.reduce((sum, item) => sum + item.price * item.quantity, 0) * 100) / 100;

      pushGA4Event('add_to_wishlist', {
        currency: config.defaultCurrency,
        value: value,
        items: items,
      });
    },

    removeFromWishlist: function (itemData, params = {}) {
      const eventParams = {
        product_id: params.product_id || itemData.item_id || itemData.sku || '',
        product_name: params.product_name || itemData.item_name || itemData.product_name || '',
      };
      const itemProductId = resolveItemProductId(itemData);
      if (itemProductId) eventParams.item_product_id = itemProductId;
      pushGA4Event('remove_from_wishlist', eventParams);
    },

    beginCheckout: function (itemsData, params = {}) {
      const items = formatItems(itemsData, params.module);
      const value = Math.round(items.reduce((sum, item) => sum + item.price * item.quantity, 0) * 100) / 100;

      pushGA4Event('begin_checkout', {
        currency: config.defaultCurrency,
        value: value,
        coupon: params.coupon || '',
        items: items,
      });
    },

    search: function (searchTermOrParams, elementLocation = '') {
      if (typeof searchTermOrParams === 'object' && searchTermOrParams !== null) {
        pushGA4Event('search', {
          element_location: searchTermOrParams.element_location || '',
          search_term: searchTermOrParams.search_term || '',
          search_action: searchTermOrParams.search_action || 'search_submit',
        });
        return;
      }

      pushGA4Event('search', {
        element_location: elementLocation,
        search_term: searchTermOrParams,
        search_action: 'search_submit',
      });
    },

    scroll: function (percentScrolled) {
      pushGA4Event('scroll', {
        percent_scrolled: percentScrolled,
      });
    },

    cookieInteraction: function (params = {}) {
      pushGA4Event('cookie_interaction', {
        interaction_type: params.interaction_type || '',
        consent_action: params.consent_action || '',
        banner_version: params.banner_version || '',
        button_name: params.button_name || '',
        banner_location: params.banner_location || '',
      });
    },

    viewBanner: function (bannerName, position) {
      pushGA4Event('view_banner', {
        banner_location: position,
        banner_name: bannerName,
      });
    },

    promoStripClick: function (params = {}) {
      pushGA4Event('promo_strip_click', {
        link_url: params.link_url || '',
        link_text: params.link_text || '',
        promo_position: params.promo_position || '',
        path_location: params.path_location || '',
        promo_name: params.promo_name || '',
        button_name: params.button_name || '',
      });
    },

    bannerClick: function (params = {}) {
      pushGA4Event('banner_click', {
        banner_location: params.banner_location || '',
        banner_type: params.banner_type || 'promotional',
        banner_name: params.banner_name || '',
        button_name: params.button_name || 'Banner',
        link_url: params.link_url || '',
      });
    },

    navigationBarClick: function (params = {}) {
      pushGA4Event('navigation_bar_click', {
        link_url: params.link_url || '',
        link_text: params.link_text || '',
        navigation_category: params.navigation_category || '',
        navigation_sub_category: params.navigation_sub_category || '',
        navigation_sub_sub_category: params.navigation_sub_sub_category || '',
      });
    },

    topFunction: function (buttonName) {
      pushGA4Event('top_function', {
        button_name: buttonName,
      });
    },

    selectOtherBrand: function (url, buttonName) {
      pushGA4Event('select_other_brand', {
        link_url: url,
        button_name: sanitizeBrandButtonName(buttonName) || buttonName || 'Brand',
      });
    },

    expressCheckout: function (buttonName, moduleOrOptions = 'Cart Popup') {
      let module = 'Cart Popup';
      const payload = {
        button_name: buttonName,
      };

      if (typeof moduleOrOptions === 'object' && moduleOrOptions !== null) {
        module = moduleOrOptions.module || 'Cart Popup';
        if (moduleOrOptions.product_id) payload.product_id = moduleOrOptions.product_id;
        if (moduleOrOptions.product_name) payload.product_name = moduleOrOptions.product_name;
        if (moduleOrOptions.item_product_id) payload.item_product_id = moduleOrOptions.item_product_id;

        const itemData = moduleOrOptions.items || moduleOrOptions.itemData;
        if (itemData && !payload.product_id && !payload.product_name) {
          const items = Array.isArray(itemData) ? itemData : [itemData];
          const primary = items[0];
          if (primary) {
            payload.product_id = primary.item_id || '';
            payload.product_name = primary.item_name || '';
            const itemProductId = resolveItemProductId(primary);
            if (itemProductId) payload.item_product_id = itemProductId;
          }
        }
      } else {
        module = moduleOrOptions || 'Cart Popup';
      }

      payload.module = module;
      pushGA4Event('express_checkout', payload);
    },

    floatBar: function (buttonName) {
      pushGA4Event('float_bar', {
        button_name: buttonName,
      });
    },

    generateLead: function (params = {}) {
      pushGA4Event('generate_lead', {
        lead_source: params.lead_source || '',
        lead_type: params.lead_type || '',
        contact_source: params.contact_source || '',
        url: params.url || window.location.href,
      });
    },

    bottomNavigation: function (navigationGroup, navigationName) {
      pushGA4Event('bottom_navigation', {
        navigation_group: navigationGroup,
        navigation_name: navigationName,
      });
    },

    followUs: function (buttonName) {
      pushGA4Event('follow_us', {
        button_name: buttonName,
      });
    },

    login: function (method) {
      const userId = resolveCustomerUserId();
      const payload = {
        event: 'ga4Event',
        event_name: 'login',
        event_parameters: { method: method || 'email' },
      };
      if (userId) payload.user_id = userId;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event_parameters: null });
      window.dataLayer.push(payload);
      log('GA4 Event:', 'login', payload);
    },

    signUp: function () {
      const userId = resolveCustomerUserId();
      const payload = {
        event: 'ga4Event',
        event_name: 'sign_up',
        event_parameters: {},
      };
      if (userId) payload.user_id = userId;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event_parameters: null });
      window.dataLayer.push(payload);
      log('GA4 Event:', 'sign_up', payload);
    },

    selectContent: function (contentTypeOrParams, contentName) {
      if (typeof contentTypeOrParams === 'object' && contentTypeOrParams !== null) {
        pushGA4Event('select_content', {
          content_type: contentTypeOrParams.content_type || contentTypeOrParams.module || '',
          content_name: contentTypeOrParams.content_name || contentTypeOrParams.button_name || '',
          link_url: contentTypeOrParams.link_url || '',
        });
        return;
      }

      pushGA4Event('select_content', {
        content_type: contentTypeOrParams || '',
        content_name: contentName || '',
      });
    },

    checkoutButton: function (params = {}) {
      pushGA4Event('checkout_button', {
        module: params.module || '',
        button_name: params.button_name || '',
      });
    },

    selectProduct: function (params = {}) {
      pushGA4Event('select_product', {
        module: params.module || '',
        button_name: params.button_name || '',
        product_name: params.product_name || '',
      });
    },
    
    watchVideo: function (params = {}) {
      pushGA4Event('video_view', {
        module: params.module || '',
        button_name: params.button_name || '',
      });
    },

    shopNow: function (params = {}) {
      pushGA4Event('shop_now', {
        module: params.module || '',
        button_name: params.button_name || '',
      });
    },
  };

  function wishlistItemFromFavoritesEl(host) {
    if (!host) return {};
    let variantTitle = host.getAttribute('data-variant-title') || '';
    if (variantTitle === 'Default Title') variantTitle = '';
    return {
      item_id: host.getAttribute('data-product-sku') || '',
      item_name: host.getAttribute('data-product-title') || '',
      item_category: host.getAttribute('data-product-type') || '',
      item_variant: variantTitle,
      price: parseFloat(host.getAttribute('data-product-price')) || 0,
      quantity: 1,
      discount: parseFloat(host.getAttribute('data-discount')) || 0,
      index: parseInt(host.getAttribute('data-index'), 10) || 0,
      item_list_id: host.getAttribute('data-list-id') || '',
      item_list_name: host.getAttribute('data-list-name') || '',
      item_product_id: resolveItemProductId(host.getAttribute('data-product-id')),
      item_variant_id: resolveItemVariantId(host.getAttribute('data-variant-id')),
    };
  }

  function shouldTrackFavoritesWishlist(host) {
    return Boolean(
      host &&
        (host.hasAttribute('data-v-gtm-wishlist') || host.hasAttribute('data-product-sku'))
    );
  }

  const trackerApi = {
    init: function (options = {}) {
      Object.assign(config, options);
      log('GA4 Tracker initialized', config);
    },
    ...GA4,
    config: config,
    pushEvent: pushGA4Event,
  };

  window.GTMTrackerGA4 = trackerApi;
  window.vGtm = {
    push: pushGA4Event,
    wishlist: {
      trackRemoveFromElement: function (host) {
        if (!shouldTrackFavoritesWishlist(host) || typeof trackerApi.removeFromWishlist !== 'function') return;
        trackerApi.removeFromWishlist(wishlistItemFromFavoritesEl(host));
      },
    },
    pushRaw: function (obj) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(obj);
      log('vGtm:raw', obj);
    },
    updateConsent: function (state) {
      if (typeof gtag === 'function') {
        gtag('consent', 'update', state);
      }
    },
    get debug() {
      return config.debug || new URLSearchParams(location.search).has('vgtm_debug');
    },
    cart: {
      trackAdd: function () {},
      trackView: function () {},
      trackLineChange: function () {},
    },
  };

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  function gtmAttr(el, key) {
    return (el.getAttribute('data-v-gtm-' + key) || '').trim();
  }

  function bindLegacyGa4Events(tracker) {
    let isCartButtonTriggered = false;
    let cartButtonTimeout = null;

    function getProductDataFromElement(element) {
      let variantTitle = element.getAttribute('data-variant-title') || '';
      if (variantTitle === 'Default Title') {
        variantTitle = '';
      }
      const productId = element.getAttribute('data-product-id') || '';
      let price = parseFloat(element.getAttribute('data-product-price'));
      if (!price) {
        const variantPriceCents = parseFloat(element.getAttribute('data-variant-price'));
        if (variantPriceCents) price = variantPriceCents / 100;
      }
      return {
        item_id: element.getAttribute('data-product-sku') || '',
        item_name: element.getAttribute('data-product-title') || '',
        item_category: element.getAttribute('data-product-type') || '',
        item_variant: variantTitle,
        price: price || 0,
        quantity: parseInt(element.getAttribute('data-quantity'), 10) || 1,
        index: parseInt(element.getAttribute('data-index'), 10) || 0,
        discount: parseFloat(element.getAttribute('data-discount')) || 0,
        item_product_id: resolveItemProductId(productId),
        item_variant_id: resolveItemVariantId(element.getAttribute('data-variant-id')),
      };
    }

    function resolveExpressCheckoutItem(source, module) {
      if (!source) return null;

      let item = buildItemFromTarget(source);
      if (!item.item_id && !item.item_name) {
        item = Object.assign({}, getProductDataFromElement(source), item);
      }
      if (!item.item_id && !item.item_name) return null;

      item.item_list_id = item.item_list_id || module || 'Product Details Page';
      item.item_list_name = item.item_list_name || item.item_list_id;
      item.index = item.index || 1;
      item.quantity = item.quantity || 1;
      return item;
    }

    document.addEventListener(
      'mousedown',
      function (e) {
        const target = e.target.closest('[data-cart-quantity-change]');
        if (target) {
          isCartButtonTriggered = true;
          if (cartButtonTimeout) {
            clearTimeout(cartButtonTimeout);
          }
          cartButtonTimeout = setTimeout(function () {
            isCartButtonTriggered = false;
          }, 200);
        }
      },
      true
    );

    document.addEventListener('click', function (e) {
      const path = e.composedPath ? e.composedPath() : [e.target];
      let target = null;

      for (let i = 0; i < path.length; i++) {
        if (path[i].hasAttribute && path[i].hasAttribute('data-ga4-track')) {
          target = path[i];
          break;
        }
      }

      if (!target) return;

      const eventType = target.getAttribute('data-ga4-event');
      const module = target.getAttribute('data-ga4-module') || '';

      switch (eventType) {
        case 'select_item': {
          let selectItem = target;
          if (!selectItem.hasAttribute('data-product-sku')) {
            selectItem = target.closest('[data-product-sku]');
          }
          if (selectItem) {
            const productData = getProductDataFromElement(selectItem);
            tracker.selectItem(productData, {
              module: module,
              button_name:
                target.getAttribute('data-button-name') ||
                selectItem.getAttribute('data-button-name') ||
                '',
            });
          }
          break;
        }

        case 'checkout_button':
          if (
            target.hasAttribute('data-v-gtm-checkout') ||
            target.getAttribute('data-v-gtm-event') === 'checkout_button'
          ) {
            break;
          }
          tracker.checkoutButton({
            module: module || 'Cart Page',
            button_name: target.getAttribute('data-button-name') || 'Checkout',
          });
          break;

        case 'add_to_wishlist': {
          const wishlistItem = target.closest('[data-product-sku]');
          if (wishlistItem) {
            const productData = getProductDataFromElement(wishlistItem);
            tracker.addToWishlist(productData, {
              module: module || 'Favorite Page',
            });
          }
          break;
        }

        case 'banner_click':
        case 'promo_strip_click':
          tracker[eventType === 'banner_click' ? 'bannerClick' : 'promoStripClick']({
            link_text: target.textContent.trim(),
            link_url: target.getAttribute('data-link-url') || target.href || '',
            promo_position: target.getAttribute('data-promo-position') || '',
            promo_name: target.getAttribute('data-promo-name') || '',
            banner_name: target.getAttribute('data-banner-name') || '',
            banner_location: target.getAttribute('data-banner-location') || '',
            banner_type: target.getAttribute('data-banner-type') || '',
            button_name: target.getAttribute('data-button-name') || '',
            path_location: target.getAttribute('data-path-location') || '',
          });
          break;

        case 'navigation_bar_click':
          tracker.navigationBarClick({
            link_url: target.href || '',
            link_text: target.textContent.trim(),
            navigation_category: target.getAttribute('data-nav-category') || '',
            navigation_sub_category: target.getAttribute('data-nav-sub') || '',
            navigation_sub_sub_category: target.getAttribute('data-nav-sub-sub') || '',
          });
          break;

        case 'top_function':
          tracker.topFunction(target.getAttribute('data-button-name') || target.textContent.trim());
          break;

        case 'float_bar':
          tracker.floatBar(target.getAttribute('data-button-name') || target.textContent.trim());
          break;

        case 'express_checkout':
          tracker.expressCheckout(target.getAttribute('data-button-name') || '', module || 'Cart Popup');
          break;

        case 'bottom_navigation':
          tracker.bottomNavigation(
            target.getAttribute('data-nav-group') || '',
            target.getAttribute('data-nav-name') || target.textContent.trim()
          );
          break;

        case 'follow_us':
          tracker.followUs(target.getAttribute('data-button-name') || target.textContent.trim());
          break;

        case 'select_other_brand':
          tracker.selectOtherBrand(target.href || '', otherBrandButtonName(target));
          break;

        case 'select_content':
          tracker.selectContent({
            content_type: target.getAttribute('data-content-type') || module,
            content_name: target.getAttribute('data-content-name') || target.textContent.trim(),
            link_url: target.href || target.getAttribute('data-link-url') || '',
          });
          break;

        case 'select_product':
          tracker.selectProduct({
            module: module || target.getAttribute('data-ga4-module') || '',
            button_name: target.getAttribute('data-button-name') || target.textContent.trim() || '',
            product_name: target.getAttribute('data-product-name') || '',
          });
          break;

        case 'shop_now':
          tracker.shopNow({
            module: module || target.getAttribute('data-ga4-module') || '',
            button_name: target.getAttribute('data-button-name') || target.textContent.trim() || '',
          });
          break;
      }
    });

    document.addEventListener('submit', function (e) {
      const form = e.target;

      if (form.matches('[data-newsletter-form]')) {
        const moduleName = form.getAttribute('data-module-name') || 'Newsletter';
        tracker.generateLead({
          lead_source: moduleName,
          lead_type: 'subscription',
          contact_source: 'email',
          url: window.location.href,
        });
      }
    });

    document.addEventListener('click', function (e) {
      const target = e.target;
      const moreOptionsLink =
        target.id === 'more-payment-options-link' ||
        target.classList.contains('shopify-payment-button__more-options') ||
        target.closest('more-payment-options-link') ||
        target.closest('.shopify-payment-button__more-options') ||
        target.closest('#more-payment-options-link');

      if (moreOptionsLink) {
        const actualTarget =
          target.id === 'more-payment-options-link' || target.classList.contains('shopify-payment-button__more-options')
            ? target
            : target.closest('more-payment-options-link') ||
              target.closest('.shopify-payment-button__more-options') ||
              target.closest('#more-payment-options-link');

        if (actualTarget) {
          const isProductPage = actualTarget.closest('product-form') !== null;

          if (isProductPage) {
            const buttonName = actualTarget.textContent.trim() || 'More payment options';
            tracker.checkoutButton({
              module: 'Product Details Page',
              button_name: buttonName,
            });
          }
        }
      }
    });

    let lastSearchTerm = '';
    let lastSearchTime = 0;
    const SEARCH_DEBOUNCE_TIME = 300;

    function trackSearch(searchTerm, location) {
      const currentTime = Date.now();
      if (searchTerm === lastSearchTerm && currentTime - lastSearchTime < SEARCH_DEBOUNCE_TIME) {
        return;
      }
      lastSearchTerm = searchTerm;
      lastSearchTime = currentTime;
      tracker.search({
        search_term: searchTerm,
        element_location: location,
        search_action: 'search_submit',
      });
    }

    const searchInputs = document.querySelectorAll('[data-search-input]');
    searchInputs.forEach(function (input) {
      if (input.closest('[data-v-gtm-scope="header"]')) return;

      input.addEventListener('keypress', function (e) {
        if (e.key === 'Enter' && input.value.trim()) {
          trackSearch(input.value.trim(), input.getAttribute('data-element-location') || '');
        }
      });
    });

    const searchSubmitButtons = document.querySelectorAll('[data-search-submit]');
    searchSubmitButtons.forEach(function (button) {
      button.addEventListener('click', function () {
        const form = button.closest('[data-search-form]');
        if (!form) return;
        const input = form.querySelector('[data-search-input]');
        if (!input || input.closest('[data-v-gtm-scope="header"]')) return;
        if (input.value.trim()) {
          trackSearch(input.value.trim(), input.getAttribute('data-element-location') || '');
        }
      });
    });

    const banners = document.querySelectorAll('[data-banner-impression]');
    if (banners.length > 0 && 'IntersectionObserver' in window) {
      const bannerObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting && !entry.target.hasAttribute('data-banner-viewed')) {
              entry.target.setAttribute('data-banner-viewed', 'true');
              tracker.viewBanner(
                entry.target.getAttribute('data-banner-name') || '',
                entry.target.getAttribute('data-banner-location') || ''
              );
            }
          });
        },
        { threshold: 0.5 }
      );

      banners.forEach(function (banner) {
        bannerObserver.observe(banner);
      });
    }

    const cookieBanner = document.querySelector('[data-cookie-banner]');
    if (cookieBanner) {
      cookieBanner.addEventListener('click', function (e) {
        const button = e.target.closest('button');
        if (button) {
          tracker.cookieInteraction({
            interaction_type: button.getAttribute('data-interaction-type') || 'click',
            consent_action: button.getAttribute('data-consent-action') || '',
            banner_version: cookieBanner.getAttribute('data-banner-version') || '1.0',
            button_name: button.textContent.trim(),
            banner_location: cookieBanner.getAttribute('data-banner-location') || 'bottom_bar',
          });
        }
      });
    }

    let lastCheckoutButtonClick = 0;
    const checkoutClickDebounce = 300;

    document.addEventListener(
      'click',
      function (e) {
        const now = Date.now();
        if (now - lastCheckoutButtonClick < checkoutClickDebounce) {
          return;
        }

        const path = e.composedPath ? e.composedPath() : [];
        let buttonName = null;
        let moduleValue = 'Cart Page';
        let isProductPage = false;
        let checkoutRoot = null;

        for (let i = 0; i < path.length; i++) {
          const element = path[i];
          if (!element.tagName) continue;

          const tagName = element.tagName.toLowerCase();

          if (!buttonName) {
            if (tagName === 'shop-pay-wallet-button') {
              buttonName = 'Shop Pay';
            } else if (tagName === 'shopify-paypal-button') {
              buttonName = 'PayPal';
            } else if (tagName === 'shopify-google-pay-button') {
              buttonName = 'Google Pay';
            } else if (element.getAttribute && element.getAttribute('slot')) {
              const slot = element.getAttribute('slot');
              if (slot && slot.startsWith('button-')) {
                buttonName = slot.replace('button-', '');
              }
            }
          }

          if (tagName === 'product-form' || tagName === 'product-form-without-atc') {
            isProductPage = true;
          }

          if (element.classList && element.classList.contains('dtc-product-form__dynamic-checkout')) {
            isProductPage = true;
            checkoutRoot = element;
            const vMod = element.getAttribute('data-v-gtm-module');
            if (vMod) moduleValue = vMod;
          }

          if (element.getAttribute && element.getAttribute('data-v-gtm-module')) {
            const vMod = element.getAttribute('data-v-gtm-module');
            if (vMod && (isProductPage || element.classList.contains('dtc-product-form__dynamic-checkout'))) {
              moduleValue = vMod;
            }
          }

          if (element.classList && element.classList.contains('additional-checkout-buttons')) {
            const ga4Module = element.getAttribute('data-ga4-module');
            if (ga4Module) {
              moduleValue = ga4Module;
            }
            break;
          }
        }

        if (isProductPage && moduleValue === 'Cart Page') {
          moduleValue = 'Product Details Page';
        }

        if (buttonName) {
          lastCheckoutButtonClick = now;
          let expressOptions = moduleValue;

          if (isProductPage) {
            const itemSource =
              checkoutRoot ||
              path.find(function (el) {
                return el.matches && el.matches('form[data-type="add-to-cart-form"]');
              });
            const item = resolveExpressCheckoutItem(itemSource, moduleValue);
            if (item) {
              expressOptions = {
                module: moduleValue,
                product_id: item.item_id || '',
                product_name: item.item_name || '',
              };
              const itemProductId = resolveItemProductId(item);
              if (itemProductId) expressOptions.item_product_id = itemProductId;
            }
          }

          tracker.expressCheckout(buttonName, expressOptions);
        }
      },
      true
    );

    observeCartDrawerViewCart(document.querySelector('cart-drawer'), 'data-view-cart-tracked', function () {
      fetch(window.Shopify.routes.root + 'cart.js')
        .then(function (response) {
          return response.json();
        })
        .then(function (cart) {
          const cartItems = (cart.items || []).map(function (item, index) {
            return shopifyLineToItem(item, index + 1);
          });

          tracker.viewCart(cartItems, {
            module: 'Cart Popup',
          });
        })
        .catch(function (error) {
          console.error('Failed to fetch cart:', error);
        });
    });
  }

  function txt(el) {
    return (el.getAttribute('data-v-gtm-text') || el.textContent || '').trim().replace(/\s+/g, ' ');
  }

  function headerNavLinkText(link) {
    const titleEl = link.querySelector('.dtc-header__mega-card-title');
    return titleEl ? txt(titleEl) : txt(link);
  }

  function sanitizeBrandButtonName(name) {
    return (name || '').replace(/\s*[-–—]\s*open in a new tab\.?\s*$/i, '').trim();
  }

  function otherBrandButtonName(link) {
    const raw =
      link.getAttribute('data-button-name') ||
      link.getAttribute('aria-label') ||
      link.querySelector('img')?.alt ||
      txt(link) ||
      'Brand';
    return sanitizeBrandButtonName(raw) || 'Brand';
  }

  const searchEventDedupe = { key: '', time: 0 };

  function getSearchInputLocation(input) {
    if (!input) return '';
    const moduleEl = input.closest('[data-v-gtm-module]');
    return (
      input.getAttribute('data-element-location') ||
      input.getAttribute('data-v-gtm-element-location') ||
      gtmAttr(input, 'element-location') ||
      gtmAttr(input, 'module') ||
      (moduleEl && moduleEl.getAttribute('data-v-gtm-module')) ||
      ''
    );
  }

  function emitSearchEvent(push, params) {
    const term = (params.search_term || '').trim();
    if (!term) return;

    const action = params.search_action || 'search_submit';
    const location = params.element_location || '';
    const dedupeKey = term + '|' + action + '|' + location;
    const now = Date.now();
    if (dedupeKey === searchEventDedupe.key && now - searchEventDedupe.time < 300) return;
    searchEventDedupe.key = dedupeKey;
    searchEventDedupe.time = now;

    push('search', {
      search_term: term,
      element_location: location,
      search_action: action,
    });
  }

  function bindHeaderSearchEvents(push) {
    function resolveHeaderSearchInput(el) {
      const input = el.matches?.('[data-search-input]')
        ? el
        : el.closest?.('[data-search-form]')?.querySelector('[data-search-input]');
      if (!input || !input.closest('[data-v-gtm-scope="header"]')) return null;
      return input;
    }

    document.addEventListener(
      'submit',
      function (e) {
        const form = e.target;
        if (!form.matches?.('[data-search-form]')) return;
        const input = resolveHeaderSearchInput(form.querySelector('[data-search-input]') || form);
        if (!input || !input.value.trim()) return;
        emitSearchEvent(push, {
          search_term: input.value.trim(),
          element_location: getSearchInputLocation(input),
          search_action: 'search_submit',
        });
      },
      true
    );

    document.addEventListener(
      'click',
      function (e) {
        const btn = e.target.closest('[data-search-submit]');
        if (!btn) return;
        const input = resolveHeaderSearchInput(btn);
        if (!input || !input.value.trim()) return;
        emitSearchEvent(push, {
          search_term: input.value.trim(),
          element_location: getSearchInputLocation(input),
          search_action: 'search_submit',
        });
      },
      true
    );

    document.addEventListener(
      'keydown',
      function (e) {
        if (e.key !== 'Enter') return;
        const input = resolveHeaderSearchInput(e.target);
        if (!input || !input.value.trim()) return;

        const predictiveSearch = input.closest('predictive-search');
        if (
          predictiveSearch &&
          predictiveSearch.querySelector('[aria-selected="true"] a, [aria-selected="true"] button')
        ) {
          return;
        }

        emitSearchEvent(push, {
          search_term: input.value.trim(),
          element_location: getSearchInputLocation(input),
          search_action: 'search_submit',
        });
      },
      true
    );

    document.addEventListener(
      'click',
      function (e) {
        const link = e.target.closest('[data-predictive-search] a');
        if (!link) return;
        const predictiveSearch = link.closest('predictive-search');
        if (!predictiveSearch || !predictiveSearch.closest('[data-v-gtm-scope="header"]')) return;
        const input = predictiveSearch.querySelector('[data-search-input]');
        if (!input) return;

        emitSearchEvent(push, {
          search_term: input.value.trim() || txt(link),
          element_location: getSearchInputLocation(input),
          search_action: 'suggested_term_click',
        });
      },
      true
    );
  }

  function bindDeclarativeSearchInputs(push) {
    if (document.documentElement.dataset.vGtmDeclarativeSearchBound) return;
    document.documentElement.dataset.vGtmDeclarativeSearchBound = '1';

    const debounceTimers = new WeakMap();

    document.addEventListener(
      'keydown',
      function (e) {
        if (e.key !== 'Enter') return;
        const input = e.target;
        if (!input.matches?.('[data-v-gtm-search]')) return;
        const term = input.value.trim();
        if (!term) return;
        emitSearchEvent(push, {
          search_term: term,
          element_location: getSearchInputLocation(input),
          search_action: 'search_submit',
        });
      },
      true
    );

    document.addEventListener(
      'submit',
      function (e) {
        const form = e.target;
        if (!form.querySelector?.('[data-v-gtm-search]')) return;
        const input = form.querySelector('[data-v-gtm-search]');
        if (!input || !input.value.trim()) return;
        emitSearchEvent(push, {
          search_term: input.value.trim(),
          element_location: getSearchInputLocation(input),
          search_action: 'search_submit',
        });
      },
      true
    );

    document.addEventListener(
      'input',
      function (e) {
        const input = e.target;
        if (!input.matches?.('[data-v-gtm-search]')) return;
        if (
          input.closest('form.dtc-search-hero__search, form.dtc-recipes-hero__search, form[action*="/search"]')
        ) {
          return;
        }

        clearTimeout(debounceTimers.get(input));
        debounceTimers.set(
          input,
          setTimeout(function () {
            const term = input.value.trim();
            if (!term) return;
            emitSearchEvent(push, {
              search_term: term,
              element_location: getSearchInputLocation(input),
              search_action: 'search_submit',
            });
          }, 800)
        );
      },
      true
    );

    document.addEventListener(
      'click',
      function (e) {
        const chip = e.target.closest('[data-v-gtm-search-chip], .dtc-search-hero__chip, .dtc-recipes-hero__chip');
        if (!chip || (!chip.closest('.dtc-search-hero') && !chip.closest('.dtc-recipes-hero'))) return;
        emitSearchEvent(push, {
          search_term: txt(chip),
          element_location: getSearchInputLocation(chip) || gtmAttr(chip, 'element-location') || '',
          search_action: 'suggested_term_click',
        });
      },
      true
    );
  }

  function bindBlogSidebarNavigation(push) {
    if (document.documentElement.dataset.vGtmBlogSidebarNavBound) return;
    document.documentElement.dataset.vGtmBlogSidebarNavBound = '1';

    document.addEventListener(
      'change',
      function (e) {
        const input = e.target;
        if (!input.matches?.('input[type="checkbox"], input[type="radio"]')) return;
        const navRoot = input.closest('[data-v-gtm-nav]');
        const isBlogNav = navRoot && navRoot.closest('.dtc-filters-blog');
        const isRecipeNav = navRoot && navRoot.closest('.dtc-recipes-grid__facets-wrapper');
        if (!isBlogNav && !isRecipeNav) return;
        if (!input.checked) return;

        const labelText = isBlogNav
          ? input.closest('label')?.querySelector('.dtc-filters-blog__option-text')
          : input.closest('label')?.querySelector('.dtc-recipes-grid__facet-label-text');
        push('select_navigation', {
          module:
            navRoot.getAttribute('data-v-gtm-module') ||
            (input.closest('[data-v-gtm-module]') || {}).getAttribute?.('data-v-gtm-module') ||
            '',
          nav_name: labelText ? txt(labelText) : input.value || '',
          link_url: window.location.href || '',
        });
      },
      true
    );
  }

  function bindHeaderTopFunctionEvents(push) {
    document.addEventListener(
      'click',
      function (e) {
        const path = e.composedPath ? e.composedPath() : [e.target];

        for (let i = 0; i < path.length; i++) {
          const el = path[i];
          if (!el || !el.closest) continue;
          if (!el.closest('[data-v-gtm-scope="header"]')) continue;

          if (el.closest('.dtc-header__logo')) {
            push('top_function', { button_name: 'Logo' });
            return;
          }

          if (el.closest('.dtc-header__cart-link, #cart-icon-bubble')) {
            push('top_function', { button_name: 'Cart' });
            return;
          }

          if (el.closest('.dtc-mobile-nav__footer a[href*="cart"]')) {
            push('top_function', { button_name: 'Cart' });
            return;
          }

          if (el.closest('.dtc-header__account-link')) {
            push('top_function', { button_name: 'Account' });
            return;
          }

          if (el.closest('.dtc-mobile-nav__footer .dtc-mobile-nav__btn--primary')) {
            push('top_function', { button_name: 'Account' });
            return;
          }

          if (el.closest('.dtc-header__search-tablet button, .dtc-header__search-tablet [data-search-drawer-trigger]')) {
            push('top_function', { button_name: 'Search' });
            return;
          }
        }
      },
      true
    );
  }

  function resolveCustomerUserId() {
    const el = document.getElementById('check-customer-data');
    const rawId = el && el.getAttribute('data-id');
    return rawId ? 'SHOPIFY_ID_' + rawId : '';
  }

  function bindCookieYesInteraction(push) {
    if (document.documentElement.dataset.vGtmCookieYesBound) return;
    document.documentElement.dataset.vGtmCookieYesBound = '1';

    document.addEventListener(
      'click',
      function (e) {
        const btn = e.target.closest(
          '.cky-btn, .cky-notice-btn-wrapper button, button[class*="cky-"], [class*="cookieyes"] button'
        );
        if (!btn) return;

        let consentAction = '';
        if (btn.classList.contains('cky-btn-accept')) consentAction = 'accept';
        else if (btn.classList.contains('cky-btn-reject')) consentAction = 'reject';
        else if (btn.classList.contains('cky-btn-customize')) consentAction = 'preferences';

        push('cookie_interaction', {
          interaction_type: consentAction ? 'consent' : 'click',
          consent_action: consentAction,
          banner_version: '1.0',
          button_name: txt(btn),
          banner_location: 'bottom_bar',
        });
      },
      true
    );
  }

  const EMAIL_SIGNUP_FORM_SELECTOR = [
    'form[data-v-gtm-form="email_signup"]',
    '.dtc-footer__subscribe-form',
    '.dtc-newsletter-split__form',
    '.dtc-subscribe-widget__form',
    '.dtc-blog-detail-article__newsletter-form',
    '.dtc-newsletter-cta__form',
  ].join(', ');

  function resolveEmailSignupModule(el) {
    if (!el) return 'Newsletter';
    const form = el.matches && el.matches('form') ? el : el.closest('form');
    const source = form || el;
    const direct = source.getAttribute('data-v-gtm-module');
    if (direct) return direct;
    const ancestor = source.closest('[data-v-gtm-module]');
    if (ancestor) return ancestor.getAttribute('data-v-gtm-module') || 'Newsletter';
    if (source.closest('.dtc-footer__subscribe-form')) return 'Bottom Navigation';
    const section = source.closest('[id^="shopify-section-"]');
    if (section) {
      const heading = section.querySelector(
        'h1, h2, h3, .dtc-subscribe-widget__heading, .dtc-newsletter-split__heading, .dtc-newsletter-cta__heading'
      );
      if (heading) return txt(heading);
    }
    return 'Newsletter';
  }

  function isEmailSignupForm(form) {
    return !!(form && form.matches && form.matches(EMAIL_SIGNUP_FORM_SELECTOR));
  }

  function pushEmailSignupOnce(push, sourceEl, storageKey) {
    const key = storageKey || 'vgtm_email_signup_default';
    if (sessionStorage.getItem(key)) return false;
    sessionStorage.setItem(key, '1');
    push('email_signup', {
      module: resolveEmailSignupModule(sourceEl),
    });
    return true;
  }

  function bindEmailSignupForms(push) {
    if (document.documentElement.dataset.vGtmEmailSignupFormsBound) return;
    document.documentElement.dataset.vGtmEmailSignupFormsBound = '1';

    document.addEventListener(
      'submit',
      function (e) {
        const form =
          e.target && e.target.closest
            ? e.target.closest('form')
            : e.target instanceof HTMLFormElement
              ? e.target
              : null;
        if (!form) return;
        if (!isEmailSignupForm(form)) return;
        if (form.checkValidity && !form.checkValidity()) return;

        const emailInput = form.querySelector('input[name="contact[email]"]');
        const email = emailInput && emailInput.value ? String(emailInput.value).trim() : '';
        if (!email) return;

        const storageKey =
          'vgtm_email_signup_' + (form.id || 'form') + '_' + email.toLowerCase();
        pushEmailSignupOnce(push, form, storageKey);
      },
      true
    );
  }

  function bindEmailSignupSuccess(push) {
    document.querySelectorAll('[data-v-gtm-email-signup-success]').forEach(function (el) {
      if (el.dataset.vGtmEmailSignupReported) return;
      el.dataset.vGtmEmailSignupReported = '1';

      const form = el.closest('form');
      const emailInput = form && form.querySelector('input[name="contact[email]"]');
      const email = emailInput && emailInput.value ? String(emailInput.value).trim().toLowerCase() : '';
      const storageKey =
        'vgtm_email_signup_' + (form && form.id ? form.id : 'success-marker') + '_' + (email || 'success');
      pushEmailSignupOnce(push, form || el, storageKey);
    });

    document
      .querySelectorAll(
        [
          '.dtc-subscribe-widget__feedback--success',
          '.dtc-newsletter-split__feedback--success',
          '.dtc-newsletter-cta__feedback--success',
          '.dtc-blog-detail-article__newsletter-message--success',
        ].join(', ')
      )
      .forEach(function (el) {
        if (el.dataset.vGtmEmailSignupReported) return;
        el.dataset.vGtmEmailSignupReported = '1';

        const form = el.closest('form');
        const storageKey = 'vgtm_email_signup_success_' + (form && form.id ? form.id : el.className);
        pushEmailSignupOnce(push, form || el, storageKey);
      });

    const params = new URLSearchParams(window.location.search);
    if (!params.has('customer_posted') && !params.has('contact_posted')) return;

    document.querySelectorAll(EMAIL_SIGNUP_FORM_SELECTOR).forEach(function (form) {
      if (form.dataset.vGtmEmailSignupReported) return;
      form.dataset.vGtmEmailSignupReported = '1';
      pushEmailSignupOnce(push, form, 'vgtm_email_signup_url_' + (form.id || 'fallback'));
    });
  }

  function bindAddContactInfo(push) {
    document.querySelectorAll('[data-v-gtm-event="add_contact_info"], [data-v-gtm-form-interaction]').forEach(function (form) {
      if (!(form instanceof HTMLFormElement)) return;
      if (form.dataset.vGtmAddContactBound) return;
      form.dataset.vGtmAddContactBound = '1';

      form.addEventListener(
        'focusin',
        function (e) {
          if (!e.target.matches('input, textarea, select')) return;
          if (form.dataset.vGtmAddContactTracked) return;
          form.dataset.vGtmAddContactTracked = '1';
          push('add_contact_info', {});
        },
        true
      );
    });
  }

  function bindFormSubmission(push) {
    function reportFormSuccess(form) {
      if (!form || form.dataset.vGtmFormSubmitted) return;
      form.dataset.vGtmFormSubmitted = '1';
      push('form_submission', {
        module: form.getAttribute('data-v-gtm-module') || 'Contact Us',
        button_name:
          form.getAttribute('data-v-gtm-button-name') ||
          (form.querySelector('button[type="submit"]') && txt(form.querySelector('button[type="submit"]'))) ||
          'Send',
      });
    }

    document.querySelectorAll('[data-v-gtm-form-submit], form[data-v-gtm-form-submit]').forEach(function (form) {
      if (form.dataset.vGtmFormSubmitBound) return;
      form.dataset.vGtmFormSubmitBound = '1';

      if (form.querySelector('.dtc-contact-support-form__form-success, [data-v-gtm-form-success]')) {
        reportFormSuccess(form);
      }

      const observer = new MutationObserver(function () {
        if (form.querySelector('.dtc-contact-support-form__form-success, [data-v-gtm-form-success]')) {
          reportFormSuccess(form);
        }
      });
      observer.observe(form, { childList: true, subtree: true });
    });
  }

  function bindLevoitSiteEvents(push) {
    function handleDtcHeaderNav(link) {
      if (!link || !link.href || link.href === '#') return;

      const linkText = headerNavLinkText(link);
      let navCategory = '';
      let navSub = '';
      let navSubSub = '';

      const topNavItem = link.closest('.dtc-header__nav-item');
      if (topNavItem) {
        const topLink = topNavItem.querySelector(':scope > .dtc-header__nav-link');
        navCategory = topLink ? txt(topLink) : '';
        if (link === topLink) {
          navSub = navCategory;
        } else {
          navSub = linkText;
        }
      } else if (link.closest('.dtc-header__learn-dropdown, .dtc-header__learn-panel')) {
        navCategory = 'Learn';
        navSub = linkText;
      } else if (link.closest('.dtc-header__mega')) {
        const topNavItemMega = link.closest('.dtc-header__nav-item');
        const topLink = topNavItemMega && topNavItemMega.querySelector(':scope > .dtc-header__nav-link');
        navCategory = topLink ? txt(topLink) : '';
        navSub = linkText;
      }

      push('navigation_bar_click', {
        link_url: link.href,
        link_text: linkText,
        navigation_category: navCategory,
        navigation_sub_category: navSub,
        navigation_sub_sub_category: navSubSub,
      });
    }

    document.addEventListener(
      'click',
      function (e) {
        const path = e.composedPath ? e.composedPath() : [e.target];

        for (let i = 0; i < path.length; i++) {
          const el = path[i];
          if (!el || !el.closest) continue;

          const brandLink = el.closest(
            '.dtc-header-top-bar__brand-link, .dtc-header__mega-brand-logo, .dtc-mobile-nav__mega-brand-logo'
          );
          if (brandLink) {
            push('select_other_brand', {
              link_url: brandLink.href || '',
              button_name: otherBrandButtonName(brandLink),
            });
            return;
          }

          const topBarSupportLink = el.closest('.dtc-header-top-bar__support-link');
          if (topBarSupportLink) {
            push('support_click', {
              link_url: topBarSupportLink.href || '',
              module: 'Header Top Bar',
              button_name: txt(topBarSupportLink) || 'Support',
            });
            return;
          }

          const promoLink = el.closest(
            'a.dtc-header-top-bar__announcement--linked, .dtc-header-top-bar__announcement a[href]'
          );
          if (promoLink) {
            const promoWrap = promoLink.closest('[data-v-gtm-promo-strip], .dtc-header-top-bar__announcement');
            const promoTextEl =
              promoWrap && promoWrap.querySelector('.dtc-header-top-bar__announcement-text');
            const promoText = promoTextEl ? txt(promoTextEl) : '';
            push('promo_strip_click', {
              link_url: promoLink.href || '',
              link_text: promoLink.classList.contains('dtc-header-top-bar__announcement--linked')
                ? promoText
                : txt(promoLink),
              promo_position: 'top_of_page',
              path_location: null,
              promo_name:
                (promoWrap && promoWrap.getAttribute('data-v-gtm-promo-name')) ||
                promoText,
              button_name: promoLink.classList.contains('dtc-header-top-bar__announcement--linked')
                ? promoText || 'Announcement'
                : txt(promoLink),
            });
            return;
          }

          const promoClose = el.closest('.dtc-header-top-bar__close');
          if (promoClose) {
            const promoWrap = promoClose.closest('dtc-header-top-bar');
            const promoTextEl =
              promoWrap && promoWrap.querySelector('.dtc-header-top-bar__announcement-text');
            push('promo_strip_click', {
              link_url: window.location.href,
              link_text: promoTextEl ? txt(promoTextEl) : '',
              promo_position: 'top_of_page',
              path_location: null,
              promo_name:
                (promoWrap &&
                  promoWrap.querySelector('[data-v-gtm-promo-name]')?.getAttribute('data-v-gtm-promo-name')) ||
                (promoTextEl ? txt(promoTextEl) : ''),
              button_name: 'Close',
            });
            return;
          }

          const header = el.closest('[data-v-gtm-scope="header"]');
          if (header) {
            const supportLink = el.closest('a[href*="support"]');
            if (supportLink && header.contains(supportLink)) {
              push('support_click', {
                link_url: supportLink.href || '',
                module: 'Top Function',
                button_name: txt(supportLink) || 'Support',
              });
              return;
            }

            const navLink = el.closest(
              '.dtc-header__nav a[href], .dtc-header__mega a[href], .dtc-header__learn-card[href], .dtc-header__learn-dropdown a[href]'
            );
            if (navLink && !navLink.closest('.dtc-header__mega-brands')) {
              handleDtcHeaderNav(navLink);
              return;
            }
          }

          const footer = el.closest('[data-v-gtm-scope="footer"]');
          if (footer) {
            const socialLink = el.closest('.dtc-footer__social-link');
            if (socialLink) {
              const moduleEl = socialLink.closest('[data-v-gtm-module]');
              push('follow_us', {
                module: (moduleEl && moduleEl.getAttribute('data-v-gtm-module')) || 'Bottom Navigation',
                button_name: socialLink.getAttribute('aria-label') || txt(socialLink),
                link_url: socialLink.href || '',
              });
              return;
            }

            const footerNavLink = el.closest('.dtc-footer__nav-link, .dtc-footer__official-websites-link');
            if (footerNavLink) {
              const col = footerNavLink.closest('.dtc-footer__nav-col');
              const headingBtn = col && col.querySelector('.dtc-footer__nav-heading');
              push('bottom_navigation', {
                navigation_group: headingBtn ? txt(headingBtn) : '',
                navigation_name: txt(footerNavLink),
                link_url: footerNavLink.href || '',
              });
              return;
            }
          }

          const faqTrigger = el.closest('[data-v-gtm-faq] .dtc-faq__trigger, [data-v-gtm-faq] [data-faq-trigger]');
          if (faqTrigger) {
            // Spec: track expand only (收起时不应触发 select_faq)
            if (faqTrigger.getAttribute('aria-expanded') === 'true') return;

            const container = faqTrigger.closest('[data-v-gtm-faq]');
            const question = faqTrigger.querySelector('.dtc-faq__question');
            push('select_faq', {
              module: (container && container.getAttribute('data-v-gtm-module')) || '',
              button_name: question ? txt(question) : txt(faqTrigger),
            });
            return;
          }

          const cookieBanner = el.closest('[data-v-gtm-cookie-banner]');
          if (cookieBanner) {
            const button = el.closest('button');
            if (button) {
              push('cookie_interaction', {
                interaction_type: button.getAttribute('data-v-gtm-interaction-type') || 'click',
                consent_action: button.getAttribute('data-v-gtm-consent-action') || '',
                banner_version: cookieBanner.getAttribute('data-v-gtm-banner-version') || '1.0',
                button_name: txt(button),
                banner_location: cookieBanner.getAttribute('data-v-gtm-banner-location') || 'bottom_bar',
              });
            }
            return;
          }
        }
      },
      true
    );

    bindHeaderSearchEvents(push);
    bindCookieYesInteraction(push);
    bindEmailSignupForms(push);
    bindEmailSignupSuccess(push);
    bindFormSubmission(push);
  }

  function collectGtmParams(target) {
    const params = {};
    const attrs = target.attributes;
    for (let i = 0; i < attrs.length; i++) {
      const name = attrs[i].name;
      if (name.indexOf('data-v-gtm-') === 0 && name !== 'data-v-gtm-event') {
        params[name.slice(11).replace(/-/g, '_')] = attrs[i].value;
      }
    }
    if (!params.module) {
      const moduleAncestor = target.closest('[data-v-gtm-module]');
      if (moduleAncestor) params.module = moduleAncestor.getAttribute('data-v-gtm-module');
    }
    if (!params.link_url && params.url) {
      params.link_url = params.url;
      delete params.url;
    }
    if (!params.link_url && target.tagName === 'A' && target.href) {
      params.link_url = target.href;
    }
    return params;
  }

  function normalizeSelectContentParams(params, el) {
    return {
      content_type: params.content_type || params.module || '',
      content_name: params.content_name || params.button_name || (el ? txt(el) : ''),
      link_url: params.link_url || (el && el.href) || '',
    };
  }

  function resolveDtcSectionModule(el) {
    const moduleEl = el.closest('[data-v-gtm-module]');
    if (moduleEl) return moduleEl.getAttribute('data-v-gtm-module') || '';

    const sectionRoot = el.closest('[id^="shopify-section-"]');
    if (!sectionRoot) return '';

    const heading = sectionRoot.querySelector(
      '.dtc-section-header__heading, .dtc-section-header__title, .dtc-multicolumn__title, .dtc-common-gradient-cta__heading, .dtc-app-ecosystem-hero__heading, .dtc-app-ecosystem-hero__mobile-heading, h1[class*="dtc-"], h2[class*="dtc-"]'
    );
    return heading ? txt(heading) : '';
  }

  function normalizeBannerClickParams(params, el) {
    const banner = el && el.closest('[data-v-gtm-banner]');
    return {
      banner_name:
        params.banner_name ||
        (banner ? banner.getAttribute('data-v-gtm-banner-name') : '') ||
        params.module ||
        '',
      banner_location:
        params.banner_location ||
        (banner ? banner.getAttribute('data-v-gtm-banner-location') : '') ||
        'hero_banner',
      banner_type:
        params.banner_type ||
        (banner ? banner.getAttribute('data-v-gtm-banner-type') : '') ||
        'promotional',
      click_type: params.click_type || 'primary_cta',
      button_name: params.button_name || (el ? txt(el) : ''),
      link_url: params.link_url || (el && el.href) || '',
    };
  }

  const DTC_SELECT_CONTENT_CTA_SELECTOR = [
    'a.dtc-multicolumn__footer-btn',
    'a.dtc-gradient-cta__button',
    'a.dtc-common-gradient-cta__button',
    'a.dtc-learn-topic-tabs__button',
    'a.dtc-vacuum-comparison__cta-btn',
    'a.dtc-affiliate-comparison__button',
    'a.dtc-promo-countdown__btn',
    'a.dtc-shop-cat-nav__cta',
    'a.dtc-collection-promo-slider__button',
    'a.dtc-scientific__cta',
    'a.dtc-expert-endorsements__ecosystem-btn',
    'a.dtc-newsletter-cta__btn',
  ].join(', ');

  function resolveFacetFilterValue(input) {
    const explicit = gtmAttr(input, 'filter-value');
    if (explicit) return explicit;

    const labelEl = input.labels && input.labels[0] ? input.labels[0] : null;
    if (labelEl) {
      const visible = labelEl.querySelector('[aria-hidden="true"]');
      if (visible) return txt(visible);
      return txt(labelEl);
    }

    return input.value || '';
  }

  function resolveSortOption(input) {
    return (
      gtmAttr(input, 'sort-option') ||
      input.getAttribute('title') ||
      (input.labels && input.labels[0] ? txt(input.labels[0]) : '') ||
      input.value ||
      ''
    );
  }

  function bindFacetSortTracking(push) {
    if (document.documentElement.dataset.vGtmFacetSortBound) return;
    document.documentElement.dataset.vGtmFacetSortBound = '1';

    document.addEventListener(
      'input',
      function (e) {
        const input = e.target;
        if (!input || !input.matches) return;
        if (!input.matches('[data-v-gtm-sort], [data-v-gtm-event="select_sort"]')) return;
        if (!input.closest('#FacetSortForm, #FacetFiltersFormMobile')) return;
        if (input.type === 'radio' && !input.checked) return;

        push('select_sort', {
          sort_option: resolveSortOption(input),
        });
      },
      true
    );
  }

  function pushSelectFilterEvent(push, target, filterAction) {
    push('select_filter', {
      module:
        gtmAttr(target, 'module') ||
        (target.closest('[data-v-gtm-module]') || {}).getAttribute?.('data-v-gtm-module') ||
        'Collection',
      filter_category:
        gtmAttr(target, 'filter-name') ||
        gtmAttr(target, 'filter-category') ||
        gtmAttr(target, 'filter_category') ||
        '',
      filter_value: gtmAttr(target, 'filter-value') || resolveFacetFilterValue(target) || txt(target),
      filter_action: filterAction || gtmAttr(target, 'filter-action') || 'deselect',
    });
  }

  function bindFacetFilterTracking(push) {
    if (document.documentElement.dataset.vGtmFacetFilterBound) return;
    document.documentElement.dataset.vGtmFacetFilterBound = '1';

    document.addEventListener(
      'input',
      function (e) {
        const input = e.target;
        if (!input || !input.matches) return;
        if (!input.matches('[data-v-gtm-filter], [data-v-gtm-event="select_filter"]')) return;
        if (!input.closest('#FacetFiltersForm, #FacetFiltersFormMobile, #FacetFiltersForm-drawer')) return;

        pushSelectFilterEvent(push, input, input.checked ? 'select' : 'deselect');
      },
      true
    );
  }

  function bindDtcSelectContentEvents(push) {
    document.addEventListener(
      'click',
      function (e) {
        const link = e.target.closest(DTC_SELECT_CONTENT_CTA_SELECTOR);
        if (!link || !link.closest('[class*="dtc-"]')) return;
        if (link.hasAttribute('data-v-gtm-event')) return;
        if (link.closest('[data-v-gtm-event]') && link.closest('[data-v-gtm-event]') !== link) return;
        if (link.closest('[data-v-gtm-item-list], [data-v-gtm-event="select_item"], .dtc-product-card, [data-v-gtm-app-download]')) {
          return;
        }

        const href = link.getAttribute('href');
        if (href === null) return;

        push('select_content', {
          content_type: resolveDtcSectionModule(link),
          content_name: txt(link),
          link_url: href === '#' ? '#' : link.href || href,
        });
      },
      true
    );
  }

  function buildItemFromTarget(target) {
    const productId =
      gtmAttr(target, 'product-id') ||
      gtmAttr(target, 'item-product-id') ||
      gtmAttr(target, 'item_product_id') ||
      '';
    const variantId =
      gtmAttr(target, 'variant-id') ||
      gtmAttr(target, 'item-variant-id') ||
      gtmAttr(target, 'item_variant_id') ||
      '';
    return {
      item_id: gtmAttr(target, 'item-id') || gtmAttr(target, 'item_id') || '',
      item_name: gtmAttr(target, 'item-name') || gtmAttr(target, 'item_name') || '',
      item_category: gtmAttr(target, 'item-category') || gtmAttr(target, 'item_category') || '',
      item_variant: gtmAttr(target, 'item-variant') || gtmAttr(target, 'item_variant') || '',
      item_brand: config.brand || 'Cosori',
      price: parseFloat(gtmAttr(target, 'price')) || 0,
      quantity: parseInt(gtmAttr(target, 'quantity'), 10) || 1,
      discount: parseFloat(gtmAttr(target, 'discount')) || 0,
      index: parseInt(gtmAttr(target, 'index'), 10) || 0,
      item_list_id: gtmAttr(target, 'item-list-id') || gtmAttr(target, 'item_list_id') || '',
      item_list_name: gtmAttr(target, 'item-list-name') || gtmAttr(target, 'item_list_name') || '',
      item_product_id: resolveItemProductId(productId),
      item_variant_id: resolveItemVariantId(variantId),
    };
  }

  function viewItemListDedupeKey(item) {
    const id = item.item_id || '';
    const name = item.item_name || '';
    const base = id ? 'id:' + id : name ? 'name:' + name : '';
    if (!base) return '';
    // Same product can appear in multiple slides — keep index so each slot can fire
    const idx = item.index != null && item.index !== '' ? String(item.index) : '';
    return idx ? base + '#' + idx : base;
  }

  function getSlideImpressionKey(slide, item) {
    if (!slide) return viewItemListDedupeKey(item);
    if (slide.id) return 'slide:' + slide.id;
    const idx =
      (item && item.index) ||
      (slide.parentElement
        ? Array.prototype.indexOf.call(slide.parentElement.children, slide) + 1
        : '');
    const base = viewItemListDedupeKey(item) || 'item';
    return 'slide:' + base + '@' + idx;
  }

  function resolveViewItemListModule(listEl) {
    if (!listEl) return '';
    const direct = listEl.getAttribute('data-v-gtm-module');
    if (direct) return direct;
    const ancestor = listEl.closest('[data-v-gtm-module]');
    return ancestor ? ancestor.getAttribute('data-v-gtm-module') || '' : '';
  }

  function findViewItemListProductRoot(el) {
    if (!el) return null;
    return (
      el.closest(
        '.dtc-product-card, .dtc-manual-product-card, .article_product_item, .dtc-vacuum-comparison__col, .card-wrapper.product-card-wrapper, .swiper-slide, [data-manual-grid-item]'
      ) || el
    );
  }

  function isRectIntersectingBounds(rect, bounds) {
    return (
      rect.width > 0 &&
      rect.height > 0 &&
      rect.right > bounds.left &&
      rect.left < bounds.right &&
      rect.bottom > bounds.top &&
      rect.top < bounds.bottom
    );
  }

  function getHorizontalOverlapWidth(rect, bounds) {
    const overlapLeft = Math.max(rect.left, bounds.left);
    const overlapRight = Math.min(rect.right, bounds.right);
    return Math.max(0, overlapRight - overlapLeft);
  }

  function getProductSwiperSlides(swiper) {
    if (!swiper || !swiper.slides) return [];
    return Array.from(swiper.slides).filter(function (slide) {
      if (!slide || slide.classList.contains('swiper-slide-duplicate')) return false;
      return !!(
        slide.querySelector('.dtc-product-card, .dtc-manual-product-card, [data-v-gtm-item-id]') ||
        pickViewItemListSource(slide)
      );
    });
  }

  function resolveSwiperSlidesPerView(swiper) {
    if (!swiper || !swiper.params) return 1;
    const raw = swiper.params.slidesPerView;
    if (raw === 'auto') {
      if (typeof swiper.slidesPerViewDynamic === 'function') {
        return Math.max(1, swiper.slidesPerViewDynamic() || 1);
      }
      return 1;
    }
    const parsed = parseFloat(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  }

  function resolveCarouselNavDirection(btn) {
    if (!btn) return 1;
    if (btn.matches('[swiper-button-prev], .dtc-multicolumn__nav-btn--prev')) return -1;
    if (btn.matches('[swiper-button-next], .dtc-multicolumn__nav-btn--next')) return 1;
    const hint = (
      btn.getAttribute('data-v-gtm-button_name') ||
      btn.getAttribute('aria-label') ||
      txt(btn) ||
      ''
    ).toLowerCase();
    if (
      hint.indexOf('prev') !== -1 ||
      hint.indexOf('left') !== -1 ||
      hint.indexOf('arrow_left') !== -1
    ) {
      return -1;
    }
    return 1;
  }

  function getSlidesForCarouselNavClick(swiper, direction) {
    if (!swiper || !swiper.slides) return [];

    const slides = Array.from(swiper.slides);
    const perView = resolveSwiperSlidesPerView(swiper);
    const count = Math.max(1, Math.ceil(perView));
    const group = parseInt(swiper.params.slidesPerGroup, 10) || 1;
    const maxStart = Math.max(0, slides.length - count);
    const currentStart = Math.max(0, swiper.activeIndex || 0);
    let nextStart = currentStart + direction * group;
    nextStart = Math.max(0, Math.min(nextStart, maxStart));

    // Only slides that are newly entering after this click
    const currentVisible = new Set();
    for (let i = 0; i < count + (perView % 1 !== 0 ? 1 : 0); i++) {
      if (slides[currentStart + i]) currentVisible.add(slides[currentStart + i]);
    }

    const entering = [];
    const seen = new Set();
    function addEntering(slide) {
      if (!slide || slide.classList.contains('swiper-slide-duplicate')) return;
      if (currentVisible.has(slide) || seen.has(slide)) return;
      seen.add(slide);
      entering.push(slide);
    }

    for (let i = 0; i < count; i++) {
      addEntering(slides[nextStart + i]);
    }
    if (perView % 1 !== 0) {
      addEntering(slides[nextStart + count]);
    }

    const productSlides = getProductSwiperSlides(swiper);
    if (direction > 0 && productSlides.length && nextStart >= maxStart) {
      addEntering(productSlides[productSlides.length - 1]);
    }

    // Fallback: if prediction found nothing (e.g. already at edge), still return next window
    if (!entering.length) {
      for (let i = 0; i < count; i++) {
        const slide = slides[nextStart + i];
        if (slide && !slide.classList.contains('swiper-slide-duplicate') && !seen.has(slide)) {
          seen.add(slide);
          entering.push(slide);
        }
      }
    }

    return entering;
  }

  function flushViewItemListOnCarouselClick(navBtn, push, currency) {
    if (!navBtn) return false;

    const scope =
      navBtn.closest('swiper-slider') ||
      navBtn.closest('dtc-multicolumn') ||
      navBtn.closest('[id^="shopify-section-"]');
    if (!scope) return false;

    const listEl = scope.querySelector('[data-v-gtm-item-list]');
    if (!listEl) return false;

    const swiper = resolveSwiperFromListEl(listEl);
    if (!swiper || swiper.enabled === false) {
      flushViewItemListForList(listEl, push, currency);
      return false;
    }

    const direction = resolveCarouselNavDirection(navBtn);
    const enteringSlides = getSlidesForCarouselNavClick(swiper, direction);
    const items = collectViewItemListItemsFromSlides(enteringSlides, listEl);
    const pushed = pushViewItemListItems(listEl, push, currency, items);

    // Re-flush after Swiper settles with the full visible window
    setTimeout(function () {
      pushViewItemListFromSwiper(swiper, listEl, push, currency);
    }, 350);

    return pushed;
  }

  function getVisibleSwiperSlides(swiper) {
    if (!swiper || !swiper.slides) return [];

    const slides = Array.from(swiper.slides);
    const productSlides = getProductSwiperSlides(swiper);
    const seen = new Set();
    const visible = [];

    function addSlide(slide) {
      if (!slide || slide.classList.contains('swiper-slide-duplicate') || seen.has(slide)) return;
      seen.add(slide);
      visible.push(slide);
    }

    // Primary: current Swiper index window
    const perView = resolveSwiperSlidesPerView(swiper);
    const count = Math.max(1, Math.ceil(perView));
    const start = Math.max(0, swiper.activeIndex || 0);
    for (let i = 0; i < count; i++) {
      addSlide(slides[start + i]);
    }
    if (perView % 1 !== 0) {
      addSlide(slides[start + count]);
    }

    // Narrow geometric check only around the active window (avoid marking all slides)
    if (swiper.el) {
      const bounds = swiper.el.getBoundingClientRect();
      const from = Math.max(0, start - 1);
      const to = Math.min(slides.length - 1, start + count + 1);
      for (let i = from; i <= to; i++) {
        const slide = slides[i];
        if (!slide) continue;
        const slideRect = slide.getBoundingClientRect();
        const overlapWidth = getHorizontalOverlapWidth(slideRect, bounds);
        const minVisible = Math.max(16, slideRect.width * 0.08);
        if (slideRect.height > 0 && overlapWidth >= minVisible) {
          addSlide(slide);
        }
      }
    }

    if (productSlides.length && (swiper.isEnd || swiper.progress >= 0.99)) {
      addSlide(productSlides[productSlides.length - 1]);
    }

    return visible;
  }

  function isListContainerInViewport(el) {
    if (!el) return false;
    const rect = el.getBoundingClientRect();
    const viewport = {
      left: 0,
      top: 0,
      right: window.innerWidth || document.documentElement.clientWidth,
      bottom: window.innerHeight || document.documentElement.clientHeight,
    };
    return isRectIntersectingBounds(rect, viewport);
  }

  function isViewItemListProductVisible(root) {
    if (!root || root.hasAttribute('hidden')) return false;
    if (root.closest('[hidden]')) return false;

    const panel = root.closest('[data-collection-panel]');
    if (panel && !panel.classList.contains('is-active')) return false;

    const viewport = {
      left: 0,
      top: 0,
      right: window.innerWidth || document.documentElement.clientWidth,
      bottom: window.innerHeight || document.documentElement.clientHeight,
    };

    const slide = root.closest('.swiper-slide');
    const swiperEl = root.closest('.swiper');
    if (slide && swiperEl && swiperEl.classList.contains('swiper-initialized')) {
      const slideRect = slide.getBoundingClientRect();
      if (!slideRect.width || !slideRect.height) return false;
      if (!isRectIntersectingBounds(slideRect, viewport)) return false;

      if (
        slide.classList.contains('swiper-slide-visible') ||
        slide.classList.contains('swiper-slide-active') ||
        slide.classList.contains('swiper-slide-fully-visible')
      ) {
        return true;
      }

      const bounds = swiperEl.getBoundingClientRect();
      return isRectIntersectingBounds(slideRect, bounds);
    }

    const rect = root.getBoundingClientRect();
    if (!rect.width || !rect.height) return false;

    return isRectIntersectingBounds(rect, viewport);
  }

  function getViewItemListSeenKeys(listEl) {
    return new Set((listEl.getAttribute('data-v-gtm-seen-items') || '').split('|').filter(Boolean));
  }

  function markViewItemListSeen(listEl, keys) {
    const seen = getViewItemListSeenKeys(listEl);
    keys.forEach(function (key) {
      if (key) seen.add(key);
    });
    listEl.setAttribute('data-v-gtm-seen-items', Array.from(seen).join('|'));
  }

  function pickViewItemListSource(root) {
    if (!root) return null;
    return (
      root.querySelector('[data-v-gtm-event="select_item"]') ||
      (root.hasAttribute('data-v-gtm-item-id') ? root : null) ||
      root.querySelector('[data-v-gtm-item-id]')
    );
  }

  function getViewItemListProductRoots(listEl) {
    const roots = [];
    const seen = new Set();
    const cardSelectors = [
      '.dtc-product-card',
      '.dtc-manual-product-card',
      '.article_product_item',
      '.dtc-vacuum-comparison__col[data-v-gtm-item-id]',
      '.card-wrapper.product-card-wrapper',
      '.swiper-slide',
      '[data-manual-grid-item]',
    ];

    function addRoot(root, fallbackIndex) {
      if (!root || seen.has(root)) return;
      const source = pickViewItemListSource(root);
      if (!source) return;
      const key = viewItemListDedupeKey(buildItemFromTarget(source));
      if (!key || seen.has(key)) return;
      seen.add(root);
      seen.add(key);
      roots.push({ root: root, source: source, index: fallbackIndex });
    }

    for (let i = 0; i < cardSelectors.length; i++) {
      const matches = listEl.querySelectorAll(cardSelectors[i]);
      if (!matches.length) continue;
      matches.forEach(function (root, idx) {
        addRoot(root, idx + 1);
      });
      if (roots.length) return roots;
    }

    listEl.querySelectorAll('[data-v-gtm-event="select_item"]').forEach(function (el, idx) {
      addRoot(findViewItemListProductRoot(el), idx + 1);
    });
    if (roots.length) return roots;

    listEl.querySelectorAll('[data-v-gtm-item-id]').forEach(function (el, idx) {
      addRoot(findViewItemListProductRoot(el), idx + 1);
    });

    return roots;
  }

  function collectViewItemListItems(listEl, options) {
    const visibleOnly = !options || options.visibleOnly !== false;
    const items = [];
    const seen = new Set();

    getViewItemListProductRoots(listEl).forEach(function (entry) {
      if (visibleOnly && !isViewItemListProductVisible(entry.root)) return;

      const item = buildItemFromTarget(entry.source);
      if (!item.index) item.index = entry.index;

      const slide = entry.root.closest ? entry.root.closest('.swiper-slide') : null;
      const key = getSlideImpressionKey(slide, item) || viewItemListDedupeKey(item);
      if (!key || seen.has(key)) return;
      seen.add(key);
      item.__impressionKey = key;
      items.push(item);
    });

    return items;
  }

  function collectViewItemListItemsFromSlides(slides, listEl) {
    const items = [];
    const seen = new Set();

    slides.forEach(function (slide, idx) {
      if (!slide || slide.classList.contains('swiper-slide-duplicate')) return;
      if (!isViewItemListProductVisible(slide)) return;

      const source =
        pickViewItemListSource(slide) ||
        pickViewItemListSource(
          slide.querySelector('.dtc-product-card, .dtc-manual-product-card, [data-manual-grid-item]')
        );
      if (!source) return;

      const item = buildItemFromTarget(source);
      const slideIndex = Array.prototype.indexOf.call(slide.parentElement?.children || [], slide);
      if (!item.index) {
        item.index = slideIndex > -1 ? slideIndex + 1 : idx + 1;
      }

      // Prefer slide identity so duplicate SKUs in different slides still fire
      const key = getSlideImpressionKey(slide, item);
      if (!key || seen.has(key)) return;
      seen.add(key);
      item.__impressionKey = key;
      items.push(item);
    });

    return items;
  }

  function resolveViewItemListMeta(listEl, items) {
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.item_list_id || item.item_list_name) {
        const listId = item.item_list_id || item.item_list_name || '';
        const listName = item.item_list_name || item.item_list_id || '';
        return { item_list_id: listId, item_list_name: listName };
      }
    }

    const listName = listEl.getAttribute('data-v-gtm-item-list-name') || '';
    const listId = listEl.getAttribute('data-v-gtm-item-list-id') || listName;
    return { item_list_id: listId, item_list_name: listName };
  }

  function normalizeViewItemListPayload(items, listId, listName) {
    return items.map(function (item) {
      const copy = Object.assign({}, item);
      copy.item_list_id = copy.item_list_id || listId;
      copy.item_list_name = copy.item_list_name || listName;
      return copy;
    });
  }

  function pushViewItemListItems(listEl, push, currency, items) {
    if (!listEl || !items.length) return false;

    const panel = listEl.closest('[data-collection-panel]');
    if (panel && !panel.classList.contains('is-active')) return false;

    const seen = getViewItemListSeenKeys(listEl);
    const newItems = items.filter(function (item) {
      const key = item.__impressionKey || viewItemListDedupeKey(item);
      return key && !seen.has(key);
    });
    if (!newItems.length) return false;

    markViewItemListSeen(
      listEl,
      newItems.map(function (item) {
        return item.__impressionKey || viewItemListDedupeKey(item);
      })
    );

    const payloadItems = newItems.map(function (item) {
      const copy = Object.assign({}, item);
      delete copy.__impressionKey;
      return copy;
    });

    const { item_list_id, item_list_name } = resolveViewItemListMeta(listEl, payloadItems);
    const normalizedPayload = normalizeViewItemListPayload(payloadItems, item_list_id, item_list_name);

    push('view_item_list', {
      item_list_name: item_list_name,
      item_list_id: item_list_id,
      module: resolveViewItemListModule(listEl),
      currency: currency,
      items: normalizedPayload,
    });
    return true;
  }

  function pushViewItemListFromElement(listEl, push, currency, options) {
    if (!listEl) return false;

    const panel = listEl.closest('[data-collection-panel]');
    if (panel && !panel.classList.contains('is-active')) return false;

    const items = collectViewItemListItems(listEl, options);
    if (!items.length) return false;

    return pushViewItemListItems(listEl, push, currency, items);
  }

  function resolveSwiperFromListEl(listEl) {
    if (!listEl) return null;
    const swiperHost = listEl.closest('swiper-slider');
    const swiperRoot =
      listEl.classList.contains('swiper-wrapper') ? listEl.parentElement : listEl.closest('.swiper');
    return (swiperHost && swiperHost.swiper) || (swiperRoot && swiperRoot.swiper) || null;
  }

  function pushViewItemListFromSwiper(swiper, listEl, push, currency) {
    if (!swiper || !listEl) return false;
    const items = collectViewItemListItemsFromSlides(getVisibleSwiperSlides(swiper), listEl);
    return pushViewItemListItems(listEl, push, currency, items);
  }

  function flushViewItemListForList(listEl, push, currency) {
    if (!listEl) return;

    const run = function () {
      if (!isListContainerInViewport(listEl)) return;

      const swiper = resolveSwiperFromListEl(listEl);
      if (swiper && swiper.enabled !== false && swiper.el && swiper.el.classList.contains('swiper-initialized')) {
        pushViewItemListFromSwiper(swiper, listEl, push, currency);
        return;
      }
      pushViewItemListFromElement(listEl, push, currency);
    };

    // Wait for Swiper transform to settle after arrow click
    requestAnimationFrame(function () {
      requestAnimationFrame(run);
    });
    setTimeout(run, 320);
  }

  function bindSwiperViewItemList(listEl, push, currency) {
    const swiperHost = listEl.closest('swiper-slider') || listEl.closest('dtc-multicolumn');
    const swiperRoot =
      listEl.classList.contains('swiper-wrapper') ? listEl.parentElement : listEl.closest('.swiper');
    if (!swiperRoot) return;

    const flush = function () {
      flushViewItemListForList(listEl, push, currency);
    };

    if (!swiperRoot.dataset.vGtmSwiperListBound) {
      const tryBind = function () {
        const swiper = resolveSwiperFromListEl(listEl);
        if (!swiper) return false;
        if (swiper.__vGtmViewItemListBound) return true;

        swiper.__vGtmViewItemListBound = true;
        swiper.on('slideChange', flush);
        swiper.on('slideChangeTransitionEnd', flush);
        swiper.on('transitionEnd', flush);
        swiper.on('reachEnd', flush);
        swiper.on('fromEdge', flush);
        // Only auto-flush on bind if the carousel is already on-screen;
        // otherwise let the real IntersectionObserver (itemObserver) fire it
        // once the user actually scrolls it into view.
        if (isListContainerInViewport(swiper.el || listEl)) {
          flush();
        }
        return true;
      };

      if (tryBind()) {
        swiperRoot.dataset.vGtmSwiperListBound = '1';
      } else {
        let attempts = 0;
        const timer = setInterval(function () {
          attempts += 1;
          if (tryBind() || attempts > 40) {
            if (tryBind()) swiperRoot.dataset.vGtmSwiperListBound = '1';
            clearInterval(timer);
          }
        }, 200);
      }
    }

    const navRoot = swiperHost || swiperRoot.parentElement;
    if (!navRoot || navRoot.dataset.vGtmNavListBound) return;
    navRoot.dataset.vGtmNavListBound = '1';
    navRoot.addEventListener(
      'click',
      function (e) {
        if (
          !e.target.closest(
            '[swiper-button-prev], [swiper-button-next], [swiper-pagination], .swiper-pagination, .dtc-multicolumn__nav-btn'
          )
        ) {
          return;
        }
        flush();
      },
      true
    );
  }

  function bindViewItemListProductObservers(push, currency) {
    function observeListProducts(listEl) {
      if (!listEl || listEl.hasAttribute('data-v-gtm-item-io-bound')) return;
      if (!('IntersectionObserver' in window)) {
        listEl.setAttribute('data-v-gtm-item-io-bound', '1');
        return;
      }
      listEl.setAttribute('data-v-gtm-item-io-bound', '1');

      const swiper = resolveSwiperFromListEl(listEl);
      const observerOptions = { threshold: [0, 0.08, 0.25] };

      const itemObserver = new IntersectionObserver(
        function (entries) {
          let shouldFlush = false;
          entries.forEach(function (entry) {
            if (entry.isIntersecting) shouldFlush = true;
          });
          if (!shouldFlush) return;
          if (!isListContainerInViewport(listEl)) return;

          const observedSwiper = resolveSwiperFromListEl(listEl);
          if (observedSwiper && observedSwiper.enabled !== false && observedSwiper.el) {
            pushViewItemListFromSwiper(observedSwiper, listEl, push, currency);
            return;
          }
          pushViewItemListFromElement(listEl, push, currency);
        },
        observerOptions
      );

      const slides = getProductSwiperSlides(
        swiper || { slides: listEl.querySelectorAll('.swiper-slide') }
      );
      if (slides.length) {
        slides.forEach(function (slide) {
          if (slide.dataset.vGtmItemIoObserved) return;
          slide.dataset.vGtmItemIoObserved = '1';
          itemObserver.observe(slide);
        });
      } else {
        getViewItemListProductRoots(listEl).forEach(function (entry) {
          if (entry.root.dataset.vGtmItemIoObserved) return;
          entry.root.dataset.vGtmItemIoObserved = '1';
          itemObserver.observe(entry.root);
        });
      }
    }

    document.querySelectorAll('[data-v-gtm-item-list]').forEach(function (listEl) {
      bindSwiperViewItemList(listEl, push, currency);
      observeListProducts(listEl);
      // If Swiper was not ready yet, re-observe once it initializes
      if (!resolveSwiperFromListEl(listEl)) {
        let attempts = 0;
        const timer = setInterval(function () {
          attempts += 1;
          if (resolveSwiperFromListEl(listEl) || attempts > 40) {
            clearInterval(timer);
            listEl.removeAttribute('data-v-gtm-item-io-bound');
            observeListProducts(listEl);
            bindSwiperViewItemList(listEl, push, currency);
            if (isListContainerInViewport(listEl)) {
              flushViewItemListForList(listEl, push, currency);
            }
          }
        }, 200);
      } else if (isListContainerInViewport(listEl)) {
        flushViewItemListForList(listEl, push, currency);
      }
    });

    // Document-level fallback for multicolumn / swiper arrow clicks
    if (!document.documentElement.dataset.vGtmSwiperNavFlushBound) {
      document.documentElement.dataset.vGtmSwiperNavFlushBound = '1';
      document.addEventListener(
        'click',
        function (e) {
          const navBtn = e.target.closest(
            '.dtc-multicolumn__nav-btn, [swiper-button-prev], [swiper-button-next]'
          );
          if (!navBtn) return;
          flushViewItemListOnCarouselClick(navBtn, push, currency);
        },
        true
      );
    }

    document.addEventListener(
      'click',
      function (e) {
        const control = e.target.closest('[data-pagination-action], [data-pagination-page], [data-collection-trigger]');
        if (!control) return;

        const panel =
          control.closest('[data-collection-panel]') ||
          control.closest('dtc-user-manual-grid')?.querySelector('[data-collection-panel].is-active');
        const listEl = panel?.querySelector('[data-v-gtm-item-list]');
        if (!listEl) return;

        if (control.matches('[data-collection-trigger]')) {
          listEl.removeAttribute('data-v-gtm-seen-items');
          listEl.removeAttribute('data-v-gtm-item-io-bound');
        }

        flushViewItemListForList(listEl, push, currency);
      },
      true
    );
  }

  function bindAuthLinkClicks() {
    if (document.documentElement.dataset.vGtmAuthLinksBound) return;
    document.documentElement.dataset.vGtmAuthLinksBound = '1';

    document.addEventListener(
      'click',
      function (e) {
        const authEl = e.target.closest('[data-v-gtm-auth]');
        if (!authEl) return;
        const authType = authEl.getAttribute('data-v-gtm-auth');
        if (!authType) return;
        try {
          sessionStorage.setItem('vgtm_pending_auth', authType);
        } catch (err) {
          log('vgtm_pending_auth storage error', err);
        }
      },
      true
    );
  }

  function bindFavouritesGridTracking(push) {
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';

    function pushFavouritesView(listEl) {
      if (!listEl) return;
      pushViewItemListFromElement(listEl, push, currency);
    }

    document.querySelectorAll('dtc-favourites-grid').forEach(function (grid) {
      if (grid.dataset.vGtmFavouritesBound) return;
      grid.dataset.vGtmFavouritesBound = '1';

      const listEl = grid.querySelector('[data-v-gtm-item-list]');
      const listRoot = grid.querySelector('.dtc-favourites-grid__list');
      if (!listEl || !listRoot) return;

      const observer = new MutationObserver(function () {
        pushFavouritesView(listEl);
      });
      observer.observe(listRoot, { childList: true });
      pushFavouritesView(listEl);
    });
  }

  function bindDtcEvents(push) {
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';

    bindDtcSelectContentEvents(push);

    document.addEventListener(
      'click',
      function (e) {
        const el = e.target;

        const tabBtn = el.closest(
          '[data-v-gtm-tabs] [role="tab"], .dtc-air-solution-tabs__nav-btn, .dtc-product-tabs__tab, .dtc-learn-topic-tabs__tab, .dtc-better-air-product-grid__tab, [data-v-gtm-tab]'
        );
        if (tabBtn) {
          const scope = tabBtn.closest('[data-v-gtm-tabs], [data-v-gtm-module], [class*="dtc-"]');
          push('tab_click', {
            module: (scope && scope.getAttribute('data-v-gtm-module')) || gtmAttr(scope, 'module') || '',
            button_name: txt(tabBtn),
          });
          return;
        }

        const techAccordionItem = el.closest('.dtc-tech-feature-accordion__item[data-feature-item]');
        if (techAccordionItem && techAccordionItem.closest('dtc-tech-feature-accordion')) {
          if (!techAccordionItem.classList.contains('is-open') && !techAccordionItem.hasAttribute('data-v-gtm-event')) {
            const titleEl = techAccordionItem.querySelector('.dtc-tech-feature-accordion__item-title');
            const scope = techAccordionItem.closest('[data-v-gtm-module]');
            push('select_content', {
              content_type: (scope && scope.getAttribute('data-v-gtm-module')) || resolveDtcSectionModule(techAccordionItem),
              content_name: titleEl ? txt(titleEl) : txt(techAccordionItem),
              link_url: '',
            });
          }
          return;
        }

        const appLink = el.closest(
          '[data-v-gtm-app-download], .dtc-vesync-app-download-bar__store-badge, .dtc-vesync-app-download__store-badge, a[href*="apps.apple.com"], a[href*="play.google.com"]'
        );
        if (appLink && appLink.closest('[class*="dtc-"]')) {
          const scope = appLink.closest('[data-v-gtm-module]');
          push('app_download', {
            module: (scope && scope.getAttribute('data-v-gtm-module')) || 'VeSync App',
            button_name: appLink.getAttribute('aria-label') || txt(appLink),
            link_url: appLink.href || '',
          });
          return;
        }

        const navLink = el.closest('.dtc-shop-category-navigation a[href], [data-v-gtm-nav] a[href]');
        if (navLink && !navLink.hasAttribute('data-v-gtm-event')) {
          const scope = navLink.closest('[data-v-gtm-module]');
          push('select_navigation', {
            module: (scope && scope.getAttribute('data-v-gtm-module')) || '',
            nav_name: txt(navLink),
            link_url: navLink.href || '',
          });
          return;
        }

        const pageLink = el.closest('.dtc-pagination__link[href]');
        if (pageLink) {
          if (
            pageLink.hasAttribute('data-v-gtm-event') ||
            pageLink.closest('[data-v-gtm-pagination]')
          ) {
            return;
          }
          push('switch_page', {
            module: (pageLink.closest('[data-v-gtm-module]') || {}).getAttribute?.('data-v-gtm-module') || 'Pagination',
            button_name: txt(pageLink),
            link_url: pageLink.href || '',
          });
          return;
        }

        const multicolumnNav = el.closest('.dtc-multicolumn__nav-btn, .dtc-affiliate-testimonials__nav-btn');
        if (multicolumnNav) {
          if (!multicolumnNav.hasAttribute('data-v-gtm-event')) {
            const scope = multicolumnNav.closest('[data-v-gtm-module]');
            const navLabel = (multicolumnNav.getAttribute('aria-label') || txt(multicolumnNav) || '').toLowerCase();
            const navButtonName = navLabel.indexOf('previous') !== -1 ? 'arrow_left' : 'arrow_right';
            push('carousel_navigation', {
              module: (scope && scope.getAttribute('data-v-gtm-module')) || '',
              button_name: navButtonName,
            });
          }
          flushViewItemListOnCarouselClick(multicolumnNav, push, currency);
          return;
        }

        const fileLink = el.closest('[data-v-gtm-file-download], a[data-v-gtm-event="file_download"]');
        if (fileLink) {
          const fileUrl = fileLink.href || gtmAttr(fileLink, 'link-url') || gtmAttr(fileLink, 'link_url') || '';
          const fileName =
            gtmAttr(fileLink, 'file-name') ||
            gtmAttr(fileLink, 'file_name') ||
            (fileUrl ? fileUrl.split('/').pop().split('?')[0] : '') ||
            txt(fileLink);
          const fileType =
            gtmAttr(fileLink, 'file-type') ||
            gtmAttr(fileLink, 'file_type') ||
            (fileName.indexOf('.') !== -1 ? fileName.split('.').pop() : '');
          push('file_download', {
            module:
              gtmAttr(fileLink, 'module') ||
              (fileLink.closest('[data-v-gtm-module]') || {}).getAttribute?.('data-v-gtm-module') ||
              '',
            file_type: fileType,
            file_name: fileName,
            link_url: fileUrl,
            product_id: gtmAttr(fileLink, 'product-id') || gtmAttr(fileLink, 'product_id') || 'none',
            product_name: gtmAttr(fileLink, 'product-name') || gtmAttr(fileLink, 'product_name') || 'none',
            button_name: gtmAttr(fileLink, 'button-name') || gtmAttr(fileLink, 'button_name') || txt(fileLink) || 'Download',
          });
          return;
        }

        const social = el.closest(
          '.dtc-social-links__link, .dtc-social-links__button, .dtc-blog-detail-article__social-link, .dtc-contact-support-form__social-link, [data-v-gtm-event="follow_us"]'
        );
        if (social && !social.closest('[data-v-gtm-scope="footer"]') && !social.hasAttribute('data-v-gtm-event')) {
          push('follow_us', {
            module: gtmAttr(social, 'module') || (social.closest('[data-v-gtm-module]') || {}).getAttribute?.('data-v-gtm-module') || '',
            button_name: social.getAttribute('aria-label') || txt(social),
            link_url: social.href || '',
          });
          return;
        }

        const langControl = el.closest('[data-v-gtm-event="select_language"]');
        if (langControl) {
          push('select_language', collectGtmParams(langControl));
          return;
        }
      },
      true
    );

    bindDeclarativeSearchInputs(push);
    bindBlogSidebarNavigation(push);

    document.querySelectorAll('[data-v-gtm-form-interaction] form, form[data-v-gtm-form-interaction]').forEach(function (form) {
      if (form.hasAttribute('data-v-gtm-form-submit')) return;
      if (form.dataset.vGtmFormBound) return;
      form.dataset.vGtmFormBound = '1';
      form.addEventListener(
        'focusin',
        function (e) {
          if (!e.target.matches('input, textarea, select')) return;
          // Skip validation refocus after submit click (avoid form_interaction + form_submission)
          if (e.relatedTarget && e.relatedTarget.matches('button[type="submit"]')) return;

          push('form_interaction', {
            module: form.getAttribute('data-v-gtm-module') || '',
            form_name: form.getAttribute('data-v-gtm-form-name') || form.getAttribute('name') || '',
            field_name: e.target.name || e.target.id || '',
            interaction_type: 'focus',
          });
        },
        true
      );
    });

    bindFacetFilterTracking(push);
    bindFacetSortTracking(push);
    bindAddContactInfo(push);

    if ('IntersectionObserver' in window) {
      const bannerObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting || entry.target.hasAttribute('data-v-gtm-banner-viewed')) return;
            entry.target.setAttribute('data-v-gtm-banner-viewed', '1');
            push('view_banner', {
              banner_name: entry.target.getAttribute('data-v-gtm-banner-name') || '',
              banner_location: entry.target.getAttribute('data-v-gtm-banner-location') || 'hero_banner',
            });
          });
        },
        { threshold: 0.5 }
      );
      document.querySelectorAll('[data-v-gtm-banner]').forEach(function (el) {
        // swiper-slide banners are triggered by slideChange, not IntersectionObserver
        if (el.classList.contains('swiper-slide')) return;
        bannerObserver.observe(el);
      });

      // Track view_banner for swiper-based sliders via slideChange
      (function bindSwiperBannerTracking() {
        var viewedIndexes = new WeakMap();

        function trackActiveSlide(swiperEl) {
          var swiper = swiperEl.swiper;
          if (!swiper) return;
          var realIndex = swiper.realIndex != null ? swiper.realIndex : swiper.activeIndex;
          var map = viewedIndexes.get(swiperEl);
          if (!map) { map = {}; viewedIndexes.set(swiperEl, map); }
          if (map[realIndex]) return;
          map[realIndex] = true;
          var slide = swiper.slides && swiper.slides[swiper.activeIndex];
          if (!slide) return;
          var bannerName = slide.getAttribute('data-v-gtm-banner-name') || '';
          var bannerLocation = slide.getAttribute('data-v-gtm-banner-location') || 'hero_banner';
          push('view_banner', { banner_name: bannerName, banner_location: bannerLocation });
        }

        function attachToSwiper(swiperEl) {
          if (swiperEl._vGtmBannerBound) return;
          swiperEl._vGtmBannerBound = true;
          trackActiveSlide(swiperEl);
          swiperEl.swiper.on('slideChange', function () {
            trackActiveSlide(swiperEl);
          });
        }

        function tryAttach(swiperEl) {
          if (swiperEl.swiper) { attachToSwiper(swiperEl); return; }
          var attempts = 0;
          var timer = setInterval(function () {
            attempts++;
            if (swiperEl.swiper) {
              clearInterval(timer);
              attachToSwiper(swiperEl);
            } else if (attempts > 50) {
              clearInterval(timer);
            }
          }, 100);
        }

        document.querySelectorAll('[data-v-gtm-banner].swiper-slide').forEach(function (slide) {
          var swiperEl = slide.closest('swiper-slider') || slide.closest('.swiper');
          if (swiperEl) tryAttach(swiperEl);
        });
      }());

      bindViewItemListProductObservers(push, currency);
    }
  }

  function bindSkillDeclarativeEvents(push, tracker) {
    // Declarative DTC events: select_collection, select_category, select_article, select_content, select_item, add_to_wishlist
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';

    document.addEventListener(
      'click',
      function (e) {
        const path = e.composedPath ? e.composedPath() : [e.target];
        let target = null;

        for (let i = 0; i < path.length; i++) {
          if (path[i].hasAttribute && path[i].hasAttribute('data-v-gtm-event')) {
            target = path[i];
            break;
          }
        }

        if (!target) return;

        const gtmEvent = target.getAttribute('data-v-gtm-event');
        if (gtmEvent) {
        // add-to-favorites reports add_to_wishlist only after a successful add (theme.js)
        if (gtmEvent === 'add_to_wishlist' && target.closest('add-to-favorites')) return;

        if (
          gtmEvent === 'add_to_cart' &&
          (target.hasAttribute('data-dtc-add-to-cart') ||
            target.closest('[data-dtc-cart-drawer-form]'))
        ) {
          return;
        }

        if (
          gtmEvent === 'increase_quantity' &&
          (target.hasAttribute('data-dtc-cart-quantity-change') ||
            target.hasAttribute('data-cart-quantity-change'))
        ) {
          return;
        }

        if (
          gtmEvent === 'remove_from_cart' &&
          (target.hasAttribute('data-dtc-cart-remove') ||
            target.hasAttribute('data-remove-all') ||
            target.closest('cart-remove-button'))
        ) {
          return;
        }

        // express_checkout: wallet buttons handled by bindLegacyGa4Events (shadow DOM button names)
        if (gtmEvent === 'express_checkout') {
          return;
        }

        let params = collectGtmParams(target);

        if (gtmEvent === 'select_category') {
          push('select_category', {
            module: params.module || '',
            link_url: params.link_url || target.href || '',
            button_name: params.button_name || params.category_name || txt(target),
          });
          return;
        }

        if (gtmEvent === 'select_collection') {
          push('select_collection', {
            module: params.module || '',
            link_url: params.link_url || target.href || '',
            button_name: params.button_name || params.collection_name || txt(target),
          });
          return;
        }

        if (gtmEvent === 'select_item' || gtmEvent === 'add_to_wishlist') {
          const item = buildItemFromTarget(target);
          const listId = params.item_list_id || params.module || item.item_list_id || '';
          const listName = params.item_list_name || item.item_list_name || listId;
          item.item_list_id = item.item_list_id || listId;
          item.item_list_name = item.item_list_name || listName;

          if (gtmEvent === 'select_item') {
            if (tracker && typeof tracker.selectItem === 'function') {
              tracker.selectItem(item, {
                module: params.module || '',
                item_list_id: listId,
                button_name: params.button_name || txt(target) || 'Card',
              });
            } else {
              push('select_item', {
                module: params.module || '',
                item_list_id: listId,
                currency: currency,
                button_name: params.button_name || txt(target) || 'Card',
                items: formatItems(item, listId),
              });
            }
            return;
          }

          if (tracker && typeof tracker.addToWishlist === 'function') {
            tracker.addToWishlist(item, {
              module: params.module || '',
              button_name: params.button_name || 'Card',
              item_list_id: listId,
            });
          } else {
            push('add_to_wishlist', Object.assign(params, {
              currency,
              button_name: params.button_name || 'Card',
              item_list_id: listId,
              items: formatItems(item, listId),
              value:
                params.value != null
                  ? parseFloat(params.value)
                  : Math.round(item.price * item.quantity * 100) / 100,
            }));
          }
          return;
        }

        if (gtmEvent === 'add_to_cart') {
          const item = buildItemFromTarget(target);
          if (tracker && typeof tracker.addToCart === 'function') {
            tracker.addToCart(item, {
              module: params.module || '',
              button_name: params.button_name || 'Add to Cart',
            });
          } else {
            push('add_to_cart', {
              currency,
              button_name: params.button_name || 'Add to Cart',
              module: params.module || '',
              value:
                params.value != null
                  ? parseFloat(params.value)
                  : Math.round(item.price * item.quantity * 100) / 100,
              items: formatItems(item, params.module || ''),
            });
          }
          return;
        }

        if (gtmEvent === 'increase_quantity') {
          const lineEl = target.closest('[data-product-sku], .cart-item, .dtc-cart-drawer__item');
          const item = lineEl ? cartLineElToItem(lineEl) : buildItemFromTarget(target);
          const eventParams = {
            product_id: params.product_id || item.item_id || '',
            product_name: params.product_name || item.item_name || '',
            quantity: parseInt(params.quantity, 10) || 1,
          };
          const itemProductId = resolveItemProductId(item);
          if (itemProductId) eventParams.item_product_id = itemProductId;
          push('increase_quantity', eventParams);
          return;
        }

        if (gtmEvent === 'remove_from_cart') {
          const lineEl = target.closest('[data-product-sku], .cart-item, .dtc-cart-drawer__item');
          const item = lineEl ? cartLineElToItem(lineEl) : buildItemFromTarget(target);
          const qty = parseInt(params.quantity, 10) || parseInt(item.quantity, 10) || 1;
          item.quantity = qty;
          if (tracker && typeof tracker.removeFromCart === 'function') {
            tracker.removeFromCart(item, {
              module: params.module || 'Cart Popup',
              button_name: params.button_name || 'Remove',
            });
          } else {
            push('remove_from_cart', {
              module: params.module || 'Cart Popup',
              currency: currency,
              button_name: params.button_name || 'Remove',
              value: Math.round(item.price * qty * 100) / 100,
              items: formatItems(item, params.module || 'Cart Popup'),
            });
          }
          return;
        }

        if (gtmEvent === 'turn_page') {
          push('turn_page', {
            content_type: params.content_type || params.module || '',
            content_name: params.content_name || params.button_name || txt(target),
          });
          return;
        }

        if (gtmEvent === 'carousel_navigation') {
          push('carousel_navigation', {
            module: params.module || '',
            button_name: params.button_name || txt(target) || '',
          });
          flushViewItemListOnCarouselClick(target, push, currency);
          return;
        }

        if (gtmEvent === 'select_content') {
          push('select_content', normalizeSelectContentParams(params, target));
          return;
        }

        if (gtmEvent === 'select_navigation') {
          push('select_navigation', {
            module: params.module || '',
            nav_name: params.navigation_name || params.nav_name || params.button_name || '',
            link_url: params.link_url || target.href || '',
          });
          return;
        }

        if (gtmEvent === 'banner_click') {
          push('banner_click', normalizeBannerClickParams(params, target));
          return;
        }

        if (gtmEvent === 'select_filter') {
          pushSelectFilterEvent(push, target);
          return;
        }

        if (gtmEvent === 'tab_click') {
          push('tab_click', {
            module: params.module || '',
            tab_name: params.tab_name || params.button_name || txt(target),
          });
          return;
        }

        if (gtmEvent === 'select_article') {
          push('select_article', {
            content_type: params.content_type || 'Recipe',
            module: params.module || '',
            article_category: params.article_category || '',
            article_name: params.article_name || params.content_name || txt(target),
            article_position:
              params.article_position != null && params.article_position !== ''
                ? parseInt(params.article_position, 10)
                : undefined,
            link_url: params.link_url || target.href || '',
          });
          return;
        }

        if (gtmEvent === 'cta_click') {
          push('cta_click', {
            content_type: params.content_type || params.module || '',
            content_name: params.content_name || params.button_name || txt(target),
            module: params.module || '',
            button_name: params.button_name || txt(target),
            link_url: params.link_url || target.href || '',
          });
          return;
        }

        if (gtmEvent === 'interaction_click') {
          push('interaction_click', {
            module: params.module || '',
            content_type: params.content_type || '',
            content_name: params.content_name || '',
            interaction_type: params.interaction_type || '',
            click_type: params.click_type || '',
          });
          return;
        }

        if (gtmEvent === 'social_share') {
          push('social_share', {
            module: params.module || '',
            content_type: params.content_type || 'Recipe',
            content_name: params.content_name || '',
            button_name: params.button_name || txt(target),
            link_url: params.link_url || '',
          });
          return;
        }

        push(gtmEvent, params);
        }
      },
      true
    );

    document.addEventListener(
      'toggle',
      function (e) {
        const details = e.target;
        if (!details || details.tagName !== 'DETAILS') return;
        if (!details.open) return;

        const container = details.closest('[data-v-gtm-faq]');
        if (container) {
          const summary = details.querySelector(':scope > summary');
          const buttonName = summary
            ? (summary.getAttribute('data-v-gtm-text') || summary.textContent || '').trim().replace(/\s+/g, ' ')
            : '';

          push('select_faq', {
            module: container.getAttribute('data-v-gtm-module') || '',
            button_name: buttonName,
          });
          return;
        }

        const productAccordion = details.closest('[data-v-gtm-product-accordion]');
        if (productAccordion) {
          const summary = details.querySelector(':scope > summary');
          const buttonName = summary
            ? (summary.getAttribute('data-v-gtm-text') || summary.textContent || '').trim().replace(/\s+/g, ' ')
            : '';

          push('select_content', {
            content_type: productAccordion.getAttribute('data-v-gtm-module') || 'Product Details Page',
            content_name: buttonName,
            link_url: '',
          });
          return;
        }

        const accordionItem = details.closest('.dtc-app-feature-accordion__item');
        if (accordionItem && details.closest('dtc-app-feature-accordion')) {
          const titleEl = details.querySelector('.dtc-app-feature-accordion__title');
          const scope = details.closest('[data-v-gtm-module]');
          push('select_content', {
            content_type: (scope && scope.getAttribute('data-v-gtm-module')) || resolveDtcSectionModule(details),
            content_name: titleEl ? txt(titleEl) : txt(details),
            link_url: '',
          });
        }
      },
      true
    );
  }

  function shopifyLineToItem(line, index) {
    if (!line) return null;
    const variantTitle = line.variant_title === 'Default Title' ? '' : line.variant_title || '';
    const discountAmount = line.line_level_discount_allocations
      ? line.line_level_discount_allocations.reduce(function (sum, allocation) {
          return sum + allocation.amount;
        }, 0) / 100
      : 0;

    const productId = line.product_id || '';
    return {
      item_id: line.sku || String(line.variant_id || line.id || ''),
      item_name: line.product_title || line.title || '',
      item_category: line.product_type || '',
      item_variant: variantTitle,
      price: (line.price || 0) / 100,
      quantity: line.quantity || 1,
      discount: discountAmount,
      index: index != null ? index : line.index || 0,
      item_list_id: 'Cart',
      item_list_name: 'Cart',
      item_product_id: resolveItemProductId(productId),
      item_variant_id: resolveItemVariantId(line.variant_id),
    };
  }

  function cartLineElToItem(lineEl) {
    if (!lineEl) return null;
    const variantTitle = lineEl.getAttribute('data-variant-title') || '';
    const productId = lineEl.getAttribute('data-product-id') || '';
    return {
      item_id: lineEl.getAttribute('data-product-sku') || '',
      item_name: lineEl.getAttribute('data-product-title') || '',
      item_category: lineEl.getAttribute('data-product-type') || '',
      item_variant: variantTitle === 'Default Title' ? '' : variantTitle,
      price: parseFloat(lineEl.getAttribute('data-product-price')) || 0,
      quantity: parseInt(lineEl.getAttribute('data-quantity'), 10) || 1,
      discount: parseFloat(lineEl.getAttribute('data-discount')) || 0,
      index: parseInt(lineEl.getAttribute('data-index'), 10) || 0,
      item_list_id: lineEl.getAttribute('data-list-name') || 'Cart',
      item_list_name: lineEl.getAttribute('data-list-name') || 'Cart',
      item_product_id: resolveItemProductId(productId),
      item_variant_id: resolveItemVariantId(lineEl.getAttribute('data-variant-id')),
    };
  }

  function observeCartDrawerViewCart(drawer, trackedAttr, onOpen) {
    if (!drawer || typeof onOpen !== 'function') return;

    const observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.type !== 'attributes' || mutation.attributeName !== 'class') return;

        const target = mutation.target;
        const oldClasses = (mutation.oldValue || '').trim().split(/\s+/).filter(Boolean);
        if (oldClasses.includes('active') || !target.classList.contains('active')) return;
        if (target.hasAttribute(trackedAttr)) return;
        if (target.hasAttribute('data-v-gtm-suppress-view-cart')) {
          target.removeAttribute('data-v-gtm-suppress-view-cart');
          return;
        }

        target.setAttribute(trackedAttr, '1');
        onOpen(target);
        setTimeout(function () {
          target.removeAttribute(trackedAttr);
        }, 500);
      });
    });

    observer.observe(drawer, {
      attributes: true,
      attributeFilter: ['class'],
      attributeOldValue: true,
    });
  }

  function bindDtcCommerceEvents(push, tracker) {
    const cartModule = 'Cart Popup';

    window.vGtm.cart.trackAdd = function (lineItem, quantity, options) {
      const opts = typeof options === 'string' ? { module: options } : options || {};
      const trigger = opts.trigger || null;
      let item = shopifyLineToItem(lineItem);

      if (trigger) {
        const fromDom = buildItemFromTarget(trigger);
        if (!item) {
          item = fromDom;
        } else if ((!item.item_id && !item.item_name) || !item.item_id) {
          item = Object.assign({}, fromDom, item);
        }
        if (fromDom.item_list_id && !item.item_list_id) item.item_list_id = fromDom.item_list_id;
        if (fromDom.item_list_name && !item.item_list_name) item.item_list_name = fromDom.item_list_name;
        if (fromDom.index && !item.index) item.index = fromDom.index;
      }

      if (!item || (!item.item_id && !item.item_name)) return;
      item.quantity = quantity || item.quantity || 1;
      if (opts.item_list_id) item.item_list_id = opts.item_list_id;
      if (opts.item_list_name) item.item_list_name = opts.item_list_name;
      if (opts.index) item.index = opts.index;
      tracker.addToCart(item, {
        module: opts.module || 'Product Details Page',
        button_name: opts.button_name || 'Add to Cart',
      });

      if (opts.view_cart_after !== false && typeof window.vGtm.cart.trackView === 'function') {
        window.vGtm.cart.trackView(opts.view_cart_module || cartModule);
      }
    };

    window.vGtm.cart.trackView = function (moduleName) {
      const mod = moduleName || cartModule;
      fetch(((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'cart.js')
        .then(function (response) {
          return response.json();
        })
        .then(function (cart) {
          const cartItems = (cart.items || []).map(function (item, index) {
            return shopifyLineToItem(item, index + 1);
          });
          tracker.viewCart(cartItems, { module: mod });
        })
        .catch(function (error) {
          console.error('Failed to fetch cart for view_cart:', error);
        });
    };

    window.vGtm.cart.trackLineChange = function (lineItem, previousQty, newQty, module) {
      let item;
      if (lineItem && lineItem.nodeType === 1) {
        item = cartLineElToItem(lineItem);
      } else if (lineItem && lineItem.item_id) {
        item = lineItem;
      } else {
        item = shopifyLineToItem(lineItem);
      }
      if (!item) return;
      const prev = parseInt(previousQty, 10) || 0;
      const next = parseInt(newQty, 10) || 0;
      const mod = module || cartModule;

      if (next <= 0 && prev > 0) {
        item.quantity = prev;
        tracker.removeFromCart(item, { module: mod, button_name: 'Remove' });
        return;
      }

      if (next > prev) {
        tracker.increaseQuantity(item, { quantity: next - prev });
        return;
      }

      if (next < prev) {
        tracker.decreaseQuantity(item, { quantity: prev - next });
      }
    };

    const scrollMarks = [25, 50, 75, 90];
    const scrollFired = {};
    function onScrollTrack() {
      const doc = document.documentElement;
      const scrollTop = window.pageYOffset || doc.scrollTop;
      const height = doc.scrollHeight - doc.clientHeight;
      if (height <= 0) return;
      const percent = Math.round((scrollTop / height) * 100);
      scrollMarks.forEach(function (mark) {
        if (percent >= mark && !scrollFired[mark]) {
          scrollFired[mark] = true;
          tracker.scroll(mark);
        }
      });
    }
    window.addEventListener('scroll', onScrollTrack, { passive: true });
    onScrollTrack();

    document.addEventListener('submit', function (e) {
      const form = e.target;
      if (!form || form.tagName !== 'FORM') return;
      const authType = form.getAttribute('data-v-gtm-auth');
      if (authType) {
        sessionStorage.setItem('vgtm_pending_auth', authType);
        return;
      }
      const action = form.getAttribute('action') || '';
      if (action.indexOf('/account/login') !== -1) {
        sessionStorage.setItem('vgtm_pending_auth', 'login');
      } else if (form.id === 'Form-create-account' || form.querySelector('[name="form_type"][value="create_customer"]')) {
        sessionStorage.setItem('vgtm_pending_auth', 'sign_up');
      }
    });

    const pendingAuth = sessionStorage.getItem('vgtm_pending_auth');
    const isLoggedIn = document.body && document.body.getAttribute('data-customer-logged-in') === 'true';
    if (pendingAuth && isLoggedIn) {
      if (pendingAuth === 'login') tracker.login('email');
      if (pendingAuth === 'sign_up') tracker.signUp();
      sessionStorage.removeItem('vgtm_pending_auth');
    }

    function observeCartDrawer(selector, moduleName) {
      observeCartDrawerViewCart(document.querySelector(selector), 'data-v-gtm-view-cart-tracked', function () {
        fetch(((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'cart.js')
          .then(function (response) {
            return response.json();
          })
          .then(function (cart) {
            const cartItems = (cart.items || []).map(function (item, index) {
              return shopifyLineToItem(item, index + 1);
            });
            tracker.viewCart(cartItems, { module: moduleName });
          })
          .catch(function (error) {
            console.error('Failed to fetch cart:', error);
          });
      });
    }

    observeCartDrawer('dtc-cart-drawer', cartModule);
    observeCartDrawer('cart-drawer', cartModule);

    document.addEventListener(
      'click',
      function (e) {
        const banner = e.target.closest('[data-v-gtm-banner]');
        if (banner && banner.contains(e.target) && !e.target.closest('[data-v-gtm-event], a[href], button')) {
          push('banner_click', {
            banner_name: banner.getAttribute('data-v-gtm-banner-name') || '',
            banner_location: banner.getAttribute('data-v-gtm-banner-location') || 'hero_banner',
            banner_type: banner.getAttribute('data-v-gtm-banner-type') || 'promotional',
            button_name: 'Banner',
          });
          return;
        }

        const checkoutBtn = e.target.closest('[data-v-gtm-event="checkout_button"], [data-v-gtm-checkout]');
        if (checkoutBtn) {
          push('checkout_button', {
            module: gtmAttr(checkoutBtn, 'module') || cartModule,
            button_name: gtmAttr(checkoutBtn, 'button-name') || gtmAttr(checkoutBtn, 'button_name') || txt(checkoutBtn),
          });
        }
      },
      true
    );

    document.querySelectorAll('[data-v-gtm-video]').forEach(function (wrap) {
      if (wrap.dataset.vGtmVideoBound) return;
      wrap.dataset.vGtmVideoBound = '1';
      const moduleName = wrap.getAttribute('data-v-gtm-module') || '';
      const onPlay = function () {
        if (wrap.hasAttribute('data-v-gtm-video-viewed')) return;
        wrap.setAttribute('data-v-gtm-video-viewed', '1');
        tracker.watchVideo({
          module: moduleName,
          button_name: wrap.getAttribute('data-v-gtm-video-name') || 'Play',
        });
      };
      wrap.querySelectorAll('video').forEach(function (video) {
        video.addEventListener('play', onPlay);
      });
      wrap.querySelectorAll('[data-dtc-video-play], [data-v-gtm-video-play]').forEach(function (btn) {
        btn.addEventListener('click', onPlay);
      });
    });
  }

  function bindDtcSalientCarouselNavigation(push) {
    document.querySelectorAll('dtc-multicolumn.dtc-product-salient-features').forEach(function (section) {
      if (section.dataset.vGtmSalientBound) return;
      section.dataset.vGtmSalientBound = '1';

      const moduleName = section.getAttribute('data-v-gtm-module') || 'Salient Features';
      const dotsRoot = section.querySelector('.dtc-multicolumn__dots, [swiper-pagination]');
      let lastIndex = null;

      function resolveBulletIndex(bullet) {
        if (!dotsRoot || !bullet) return -1;
        const bullets = dotsRoot.querySelectorAll('.swiper-pagination-bullet');
        return Array.prototype.indexOf.call(bullets, bullet);
      }

      function syncLastIndexFromActive() {
        if (!dotsRoot) return;
        const active = dotsRoot.querySelector('.swiper-pagination-bullet-active');
        if (!active) return;
        const idx = resolveBulletIndex(active);
        if (idx >= 0) lastIndex = idx;
      }

      syncLastIndexFromActive();

      section.addEventListener(
        'click',
        function (e) {
          const bullet = e.target.closest('.swiper-pagination-bullet');
          if (!bullet) return;

          const nextIndex = resolveBulletIndex(bullet);
          if (nextIndex < 0) return;
          if (lastIndex != null && nextIndex === lastIndex) return;

          let buttonName = 'arrow_right';
          if (lastIndex != null && nextIndex < lastIndex) buttonName = 'arrow_left';
          lastIndex = nextIndex;

          push('carousel_navigation', {
            module: moduleName,
            button_name: buttonName,
          });
        },
        true
      );
    });
  }

  function bindDtcBlogPagination(push) {
    if (document.documentElement.dataset.vGtmBlogPaginationBound) return;
    document.documentElement.dataset.vGtmBlogPaginationBound = '1';

    document.addEventListener(
      'click',
      function (e) {
        const btn = e.target.closest('[data-dtc-blog-pagination] [data-page]');
        if (!btn) return;

        const paginationRoot = btn.closest('[data-dtc-blog-pagination]');
        const moduleName =
          (paginationRoot && paginationRoot.getAttribute('data-v-gtm-module')) ||
          (document.querySelector('[data-dtc-blog-list]') || {}).closest?.('[data-v-gtm-module]')?.getAttribute?.(
            'data-v-gtm-module'
          ) ||
          'Explore Our Blogs';

        push('turn_page', {
          content_type: moduleName,
          content_name: btn.getAttribute('data-page') || txt(btn),
        });
      },
      true
    );
  }

  function bindDtcRecipePlazaPagination(push) {
    if (document.documentElement.dataset.vGtmRecipePlazaPaginationBound) return;
    document.documentElement.dataset.vGtmRecipePlazaPaginationBound = '1';

    document.addEventListener(
      'click',
      function (e) {
        const link = e.target.closest('[data-v-gtm-pagination] a.dtc-pagination__link[href]:not([aria-current])');
        if (!link || link.classList.contains('dtc-pagination__nav--disabled')) return;
        if (link.hasAttribute('data-v-gtm-event')) return;

        const paginationRoot = link.closest('[data-v-gtm-pagination]');
        const moduleName =
          (paginationRoot && paginationRoot.getAttribute('data-v-gtm-module')) ||
          (paginationRoot && paginationRoot.getAttribute('data-v-gtm-content-type')) ||
          'Recipes Matching';

        push('turn_page', {
          content_type: moduleName,
          content_name:
            link.getAttribute('data-v-gtm-content_name') ||
            (link.querySelector('.dtc-pagination__nav-label')
              ? txt(link.querySelector('.dtc-pagination__nav-label'))
              : txt(link)),
        });
      },
      true
    );
  }

  function bindDtcReviewPagination(push) {
    if (document.documentElement.dataset.vGtmReviewPaginationBound) return;
    document.documentElement.dataset.vGtmReviewPaginationBound = '1';

    document.addEventListener(
      'click',
      function (e) {
        const pageLink = e.target.closest(
          '[data-bv-show="reviews"] .bv-content-pagination button, [data-bv-show="reviews"] .bv-content-pagination a, #bv-reviews .bv-content-pagination button, #bv-reviews .bv-content-pagination a, .bv-content-pagination-container button, .bv-content-pagination-container a'
        );
        if (!pageLink) return;

        push('turn_page', {
          content_type: 'Comment',
          content_name: txt(pageLink),
        });
      },
      true
    );
  }

  bindHeaderTopFunctionEvents(pushGA4Event);

  onReady(function () {
    bindLegacyGa4Events(trackerApi);
    bindLevoitSiteEvents(pushGA4Event);
    bindDtcEvents(pushGA4Event);
    bindFavouritesGridTracking(pushGA4Event);
    bindAuthLinkClicks();
    bindDtcCommerceEvents(pushGA4Event, trackerApi);
    bindSkillDeclarativeEvents(pushGA4Event, trackerApi);
    bindDtcSalientCarouselNavigation(pushGA4Event);
    bindDtcBlogPagination(pushGA4Event);
    bindDtcRecipePlazaPagination(pushGA4Event);
    bindDtcReviewPagination(pushGA4Event);
    if (config.debug || window.vGtm.debug) {
      console.log('[vGtm] Event listeners initialized');
    }
  });

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = trackerApi;
  }
})();

